/* Kleur-match leadtool: runtime (2026-NL).
 *
 * Vier invoerroutes (kleurkiezer, hex, Pantone, RAL, plus foto-upload) leiden allemaal
 * naar één hex. Daaruit volgt direct (client-side) de dichtstbijzijnde Pantone- en
 * RAL-kleur, de CMYK-waarde en een kleurenpalet. Parallel vraagt de pagina de backend
 * om passende producten; komt dat antwoord binnen, dan is het leidend voor de codes.
 * Blijft het uit, dan blijft de pagina bruikbaar: codes en palet staan er al.
 *
 * Foto-upload gebeurt volledig in de browser (canvas + kleurhistogram). Het bestand
 * wordt nooit geüpload: dat is geen implementatiedetail maar een belofte op de pagina.
 *
 * De backend levert altijd de top-3 op kleurafstand, ook als die ver weg ligt. Het
 * optionele veld `matchInfo` zegt per categorie of het beste item binnen de ΔE-drempel
 * valt; is dat niet zo, dan tonen we die producten als "alternatieven in andere kleuren"
 * in plaats van als match. Ontbreekt `matchInfo`, dan leiden we het af uit de ΔE.
 *
 * De kleurdatasets (2310 Pantone TCX voor textiel, 3233 Pantone Solid Coated voor
 * druk en 216 RAL Classic, ~104 kB, gzipt ~50 kB) staan in kleur-match-data.js en
 * worden pas bij de eerste interactie geladen, zodat de pagina zelf licht blijft.
 *
 * Pantone kent twee losse bibliotheken zonder officiële koppeltabel. Wij verkopen
 * textiel, dus TCX is hier de standaard; Solid Coated blijft kiesbaar voor wie een
 * code van zijn drukker heeft. De pagina rekent altijd in één systeem tegelijk en
 * toont de code uit het andere systeem apart, als benadering met de ΔE erbij.
 *
 * Rekenketen: sRGB (D65) -> lineair -> XYZ -> CIELAB, afstanden met CIEDE2000
 * (Sharma, Wu & Dalal 2005). Overgenomen uit de beheer-app
 * (apps/frontend/src/lib/color-match.ts), waar de implementatie tegen de 34
 * gepubliceerde testparen is getoetst.
 */
(function () {
  'use strict';

  /* ─── Kleurwiskunde ──────────────────────────────────────────────────── */

  var EXACT_DELTA_E = 0.5;

  function parseHex(invoer) {
    if (!invoer) return null;
    var rauw = String(invoer).trim().replace(/^#/, '');
    if (/^[0-9A-Fa-f]{3}$/.test(rauw)) {
      return ('#' + rauw[0] + rauw[0] + rauw[1] + rauw[1] + rauw[2] + rauw[2]).toUpperCase();
    }
    if (/^[0-9A-Fa-f]{6}$/.test(rauw)) return ('#' + rauw).toUpperCase();
    return null;
  }

  function hexToRgb(hex) {
    var p = parseHex(hex);
    if (!p) return null;
    return { r: parseInt(p.slice(1, 3), 16), g: parseInt(p.slice(3, 5), 16), b: parseInt(p.slice(5, 7), 16) };
  }

  function rgbToHex(r, g, b) {
    function deel(v) {
      var n = Math.max(0, Math.min(255, Math.round(v))).toString(16);
      return n.length === 1 ? '0' + n : n;
    }
    return ('#' + deel(r) + deel(g) + deel(b)).toUpperCase();
  }

  function srgbNaarLineair(kanaal) {
    var c = kanaal / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  var WIT = { x: 95.047, y: 100, z: 108.883 }; // D65, 2°-waarnemer

  function rgbNaarLab(r, g, b) {
    var lr = srgbNaarLineair(r), lg = srgbNaarLineair(g), lb = srgbNaarLineair(b);
    var x = (lr * 0.4124564 + lg * 0.3575761 + lb * 0.1804375) * 100;
    var y = (lr * 0.2126729 + lg * 0.7151522 + lb * 0.072175) * 100;
    var z = (lr * 0.0193339 + lg * 0.119192 + lb * 0.9503041) * 100;
    function f(t) { return t > 216 / 24389 ? Math.cbrt(t) : (841 / 108) * t + 4 / 29; }
    var fx = f(x / WIT.x), fy = f(y / WIT.y), fz = f(z / WIT.z);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }

  function hexNaarLab(hex) {
    var rgb = hexToRgb(hex);
    return rgb ? rgbNaarLab(rgb.r, rgb.g, rgb.b) : null;
  }

  function graden(r) { return (r * 180) / Math.PI; }
  function radialen(d) { return (d * Math.PI) / 180; }

  /** CIEDE2000. <1 niet zichtbaar, 1-2 zij aan zij, 2-5 duidelijk, >5 andere kleur. */
  function deltaE2000(L1, a1, b1, L2, a2, b2) {
    var C1 = Math.sqrt(a1 * a1 + b1 * b1);
    var C2 = Math.sqrt(a2 * a2 + b2 * b2);
    var Cbar = (C1 + C2) / 2;
    var Cbar7 = Math.pow(Cbar, 7);
    var G = 0.5 * (1 - Math.sqrt(Cbar7 / (Cbar7 + 6103515625))); // 25^7

    var a1p = (1 + G) * a1, a2p = (1 + G) * a2;
    var C1p = Math.sqrt(a1p * a1p + b1 * b1);
    var C2p = Math.sqrt(a2p * a2p + b2 * b2);

    var h1p = b1 === 0 && a1p === 0 ? 0 : (graden(Math.atan2(b1, a1p)) + 360) % 360;
    var h2p = b2 === 0 && a2p === 0 ? 0 : (graden(Math.atan2(b2, a2p)) + 360) % 360;

    var dLp = L2 - L1;
    var dCp = C2p - C1p;

    var dhp;
    if (C1p * C2p === 0) dhp = 0;
    else if (Math.abs(h2p - h1p) <= 180) dhp = h2p - h1p;
    else if (h2p - h1p > 180) dhp = h2p - h1p - 360;
    else dhp = h2p - h1p + 360;
    var dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(radialen(dhp) / 2);

    var Lbarp = (L1 + L2) / 2;
    var Cbarp = (C1p + C2p) / 2;

    var hbarp;
    if (C1p * C2p === 0) hbarp = h1p + h2p;
    else if (Math.abs(h1p - h2p) <= 180) hbarp = (h1p + h2p) / 2;
    else if (h1p + h2p < 360) hbarp = (h1p + h2p + 360) / 2;
    else hbarp = (h1p + h2p - 360) / 2;

    var T = 1
      - 0.17 * Math.cos(radialen(hbarp - 30))
      + 0.24 * Math.cos(radialen(2 * hbarp))
      + 0.32 * Math.cos(radialen(3 * hbarp + 6))
      - 0.2 * Math.cos(radialen(4 * hbarp - 63));

    var dTheta = 30 * Math.exp(-Math.pow((hbarp - 275) / 25, 2));
    var Cbarp7 = Math.pow(Cbarp, 7);
    var Rc = 2 * Math.sqrt(Cbarp7 / (Cbarp7 + 6103515625));

    var Sl = 1 + (0.015 * Math.pow(Lbarp - 50, 2)) / Math.sqrt(20 + Math.pow(Lbarp - 50, 2));
    var Sc = 1 + 0.045 * Cbarp;
    var Sh = 1 + 0.015 * Cbarp * T;
    var Rt = -Math.sin(radialen(2 * dTheta)) * Rc;

    return Math.sqrt(
      Math.pow(dLp / Sl, 2) + Math.pow(dCp / Sc, 2) + Math.pow(dHp / Sh, 2) +
      Rt * (dCp / Sc) * (dHp / Sh)
    );
  }

  function deltaE2000Hex(hex1, hex2) {
    var a = hexNaarLab(hex1), b = hexNaarLab(hex2);
    if (!a || !b) return Infinity;
    return deltaE2000(a[0], a[1], a[2], b[0], b[1], b[2]);
  }

  /** Naïeve RGB->CMYK (geen ICC-profiel, geen zwartopbouw), startwaarde voor de drukker. */
  function rgbNaarCmyk(r, g, b) {
    var rr = r / 255, gg = g / 255, bb = b / 255;
    var k = 1 - Math.max(rr, gg, bb);
    if (k === 1) return '0/0/0/100';
    return [
      Math.round(((1 - rr - k) / (1 - k)) * 100),
      Math.round(((1 - gg - k) / (1 - k)) * 100),
      Math.round(((1 - bb - k) / (1 - k)) * 100),
      Math.round(k * 100)
    ].join('/');
  }

  function hexNaarCmyk(hex) {
    var rgb = hexToRgb(hex);
    return rgb ? rgbNaarCmyk(rgb.r, rgb.g, rgb.b) : '';
  }

  function toonDeltaE(d) {
    if (typeof d !== 'number' || !isFinite(d)) return '';
    if (d <= EXACT_DELTA_E) return 'exact';
    return 'ΔE ' + d.toFixed(1).replace('.', ',');
  }

  /* ─── HSL, voor het kleurenpalet ─────────────────────────────────────── */

  function rgbNaarHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
    }
    return [h, s, l];
  }

  function hslNaarRgb(h, s, l) {
    h = ((h % 360) + 360) % 360;
    var c = (1 - Math.abs(2 * l - 1)) * s;
    var x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    var m = l - c / 2;
    var r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }
    return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
  }

  function draaiTint(hex, gradenDelta) {
    var rgb = hexToRgb(hex);
    if (!rgb) return hex;
    var hsl = rgbNaarHsl(rgb.r, rgb.g, rgb.b);
    var uit = hslNaarRgb(hsl[0] + gradenDelta, hsl[1], hsl[2]);
    return rgbToHex(uit[0], uit[1], uit[2]);
  }

  /**
   * Client-side palet, met exact dezelfde `role`-strings en volgorde als de backend
   * teruggeeft (basis, complementair, analoog, analoog-2, triadisch, triadisch-2).
   * Zo verspringt er niets zodra het serverantwoord de tijdelijke waarden vervangt.
   */
  function bouwPalet(hex) {
    return [
      { role: 'basis', hex: hex },
      { role: 'complementair', hex: draaiTint(hex, 180) },
      { role: 'analoog', hex: draaiTint(hex, -30) },
      { role: 'analoog-2', hex: draaiTint(hex, 30) },
      { role: 'triadisch', hex: draaiTint(hex, 120) },
      { role: 'triadisch-2', hex: draaiTint(hex, 240) }
    ];
  }

  var ROL_LABELS = {
    basis: 'Basis', complementair: 'Complementair',
    analoog: 'Analoog', 'analoog-1': 'Analoog', 'analoog-2': 'Analoog',
    triadisch: 'Triadisch', 'triadisch-1': 'Triadisch', 'triadisch-2': 'Triadisch',
    accent: 'Accent', neutraal: 'Neutraal'
  };

  function rolLabel(rol) {
    if (ROL_LABELS[rol]) return ROL_LABELS[rol];
    var tekst = String(rol || '').replace(/[-_]/g, ' ').trim();
    return tekst ? tekst.charAt(0).toUpperCase() + tekst.slice(1) : 'Kleur';
  }

  /* ─── Datasets: lui laden en indexeren ───────────────────────────────── */

  var dataBelofte = null;

  /** Sleutel voor tolerante vergelijking: alleen letters en cijfers, kleine letters. */
  function plet(waarde) { return String(waarde).toLowerCase().replace(/[^a-z0-9]/g, ''); }

  function indexeerBlob(blob, metNamen) {
    var regels = String(blob || '').split('\n');
    var uit = {
      codes: [], hexes: [], namenNl: [], namenEn: [],
      labs: new Float64Array(regels.length * 3),
      opSleutel: {}
    };
    for (var i = 0; i < regels.length; i++) {
      var regel = regels[i];
      if (!regel) continue;
      var code, hex, nlNaam = '', enNaam = '';
      if (metNamen) {
        code = regel.slice(0, 4);
        hex = '#' + regel.slice(4, 10);
        var namen = regel.slice(10).split('|');
        nlNaam = namen[0] || '';
        enNaam = namen[1] || '';
      } else {
        code = regel.slice(0, -6);
        hex = '#' + regel.slice(-6);
      }
      var lab = hexNaarLab(hex);
      if (!lab) continue;
      var n = uit.codes.length;
      uit.codes.push(code);
      uit.hexes.push(hex);
      uit.namenNl.push(nlNaam);
      uit.namenEn.push(enNaam);
      uit.labs[n * 3] = lab[0]; uit.labs[n * 3 + 1] = lab[1]; uit.labs[n * 3 + 2] = lab[2];
      // Voorvoegsel voorkomt botsing met Object-eigenschappen als "constructor".
      uit.opSleutel['k' + plet(code)] = n;
    }
    return uit;
  }

  /**
   * TCX-blob: één regel per kleur, met vaste posities in plaats van scheidingstekens.
   * `18-1535B65D48Ginger Spice` = nummer (altijd 7 tekens), 6 hex, dan de naam.
   * Een eigen functie in plaats van een derde modus in indexeerBlob: de posities
   * verschillen op elk veld, en één functie met drie modi wordt onleesbaar.
   * De uitvoerstructuur is gelijk aan die van indexeerBlob, zodat dichtstbij()
   * beide sets ongewijzigd aankan. De naam staat in namenNl.
   */
  function indexeerTcx(blob) {
    var regels = String(blob || '').split('\n');
    var uit = {
      codes: [], hexes: [], namenNl: [], namenEn: [],
      labs: new Float64Array(regels.length * 3),
      opSleutel: {}
    };
    for (var i = 0; i < regels.length; i++) {
      var regel = regels[i];
      if (regel.length < 13) continue;
      var code = regel.slice(0, 7);
      var hex = '#' + regel.slice(7, 13);
      var lab = hexNaarLab(hex);
      if (!lab) continue;
      var n = uit.codes.length;
      uit.codes.push(code);
      uit.hexes.push(hex);
      uit.namenNl.push(regel.slice(13));
      uit.namenEn.push('');
      uit.labs[n * 3] = lab[0]; uit.labs[n * 3 + 1] = lab[1]; uit.labs[n * 3 + 2] = lab[2];
      // plet() haalt het streepje weg, dus "18-1535" en "181535" komen op dezelfde
      // sleutel uit. Botsen met een Solid Coated-code kan niet: die zijn korter of
      // bevatten letters, nooit zes losse cijfers.
      uit.opSleutel['k' + plet(code)] = n;
    }
    return uit;
  }

  var DATA = null;

  function bouwIndex() {
    if (DATA) return DATA;
    var bron = window.TR26_KLEURDATA;
    if (!bron) throw new Error('kleurdata ontbreekt');
    DATA = {
      suffix: bron.pantoneSuffix || '',
      pantone: indexeerBlob(bron.pantone, false),
      tcxSuffix: bron.tcxSuffix || ' TCX',
      tcx: null,
      ral: indexeerBlob(bron.ral, true)
    };
    // Een oud asset zonder TCX-blob mag de tool niet breken: dan valt alles terug
    // op Solid Coated en verdwijnt het keuzeveld. Zo maakt de deployvolgorde van
    // de twee assets niet uit.
    if (bron.tcx) DATA.tcx = indexeerTcx(bron.tcx);
    else if (window.console && console.warn) console.warn('kleur-match: TCX-dataset ontbreekt, alleen Solid Coated beschikbaar');
    return DATA;
  }

  function laadData(url) {
    if (dataBelofte) return dataBelofte;
    dataBelofte = new Promise(function (klaar, mislukt) {
      if (window.TR26_KLEURDATA) { klaar(bouwIndex()); return; }
      var afgehandeld = false;
      window.TR26_KLEURDATA_GELADEN = function () {
        if (afgehandeld) return;
        afgehandeld = true;
        try { klaar(bouwIndex()); } catch (e) { mislukt(e); }
      };
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onerror = function () {
        if (afgehandeld) return;
        afgehandeld = true;
        mislukt(new Error('kleurdata kon niet geladen worden'));
      };
      // Vangnet: sommige oudere browsers vuren onload zonder dat de callback liep.
      s.onload = function () {
        if (afgehandeld) return;
        afgehandeld = true;
        try { klaar(bouwIndex()); } catch (e) { mislukt(e); }
      };
      document.head.appendChild(s);
    });
    return dataBelofte;
  }

  function dichtstbij(set, lab, suffix) {
    var beste = -1, besteAfstand = Infinity;
    for (var i = 0; i < set.codes.length; i++) {
      var d = deltaE2000(lab[0], lab[1], lab[2], set.labs[i * 3], set.labs[i * 3 + 1], set.labs[i * 3 + 2]);
      if (d < besteAfstand) { besteAfstand = d; beste = i; }
    }
    if (beste < 0) return null;
    return {
      code: set.codes[beste] + (suffix || ''),
      hex: set.hexes[beste],
      name: set.namenNl[beste] || '',
      nameEn: set.namenEn[beste] || '',
      deltaE: besteAfstand
    };
  }

  /* ─── Pantone: twee bibliotheken, één invoerveld ─────────────────────── */

  var SYSTEEM_STANDAARD = 'tcx';

  /** Haalt "PANTONE" en "PMS" uit de invoer en maakt er kleine letters van. */
  function schoonPantone(invoer) {
    if (!invoer) return '';
    return String(invoer)
      .toLowerCase()
      .replace(/\bpantone\b/g, ' ')
      .replace(/\bpms\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Is dit een metallic (Pantone FHI Metallic Shimmers)? De reeks 20-0xxx bestaat alleen
   * daar; TCX loopt van 11- tot 19-, dus er is geen overlap. Het achtervoegsel TPM mag in
   * de invoer ontbreken. Dezelfde regel staat in snippets/is-metallic-2026nl.liquid en in
   * de inline JS van snippets/kleurwaarschuwing-2026nl.liquid; wijzig ze samen.
   */
  var VORM_METALLIC = /^20-0\d{3}(\s*tpm)?$/;
  function isMetallicPantone(code) {
    return VORM_METALLIC.test(schoonPantone(code));
  }

  /** Vorm van een FHI-code: twee cijfers, streepje, vier cijfers, eventueel TCX/TPG/TPX. */
  var VORM_FHI = /^\d{2}\s*-\s*\d{4}(\s*(tcx|tpg|tpx))?$/;
  /** Vorm van een Solid Coated-code: een nummer, met of zonder de "c" van coated. */
  var VORM_COATED = /^\d{1,4}\s*c?$/;

  /**
   * Uit welk systeem komt deze code, te zien aan de vorm?
   *
   * Dit staat los van het keuzeveld: wie een TCX-code plakt terwijl de keuze op
   * Druk staat, hoort geen foutmelding te krijgen maar een TCX-treffer. Geeft
   * null als de vorm niets verraadt ("Black 6 C", "warm red"); dan beslist de
   * voorkeur van de aanroeper.
   */
  function herkenSysteem(invoer) {
    var schoon = schoonPantone(invoer);
    if (!schoon) return null;
    if (VORM_FHI.test(schoon)) return 'tcx';
    if (VORM_COATED.test(schoon)) return 'coated';
    return null;
  }

  function zoekCoated(sleutel) {
    var set = DATA && DATA.pantone;
    if (!set) return null;
    // De blob bewaart de basiscode zonder " C"; een ingetypte "202 C" wordt dus
    // nog eens zonder dat achtervoegsel geprobeerd.
    var suffixSleutel = plet(DATA.suffix || '');
    var n = set.opSleutel['k' + sleutel];
    if (n === undefined && suffixSleutel && sleutel.slice(-suffixSleutel.length) === suffixSleutel) {
      n = set.opSleutel['k' + sleutel.slice(0, sleutel.length - suffixSleutel.length)];
    }
    if (n === undefined) return null;
    return { system: 'coated', code: set.codes[n] + DATA.suffix, hex: set.hexes[n], name: '', deltaE: 0 };
  }

  function zoekTcx(sleutel) {
    var set = DATA && DATA.tcx;
    if (!set) return null;
    // TPG en TPX delen het nummer met TCX (ander materiaal, zelfde kleur), dus
    // die vallen op dezelfde regel en de code wordt als TCX teruggegeven.
    var kaal = sleutel.replace(/(tcx|tpg|tpx)$/, '');
    var n = set.opSleutel['k' + kaal];
    if (n === undefined) return null;
    return {
      system: 'tcx',
      code: set.codes[n] + (DATA.tcxSuffix || ''),
      hex: set.hexes[n],
      name: set.namenNl[n] || '',
      deltaE: 0
    };
  }

  /**
   * Zoekt een Pantone-code op in beide bibliotheken en zegt erbij uit welke hij
   * komt. De vorm van de code beslist als eerste; verraadt die niets, dan wint
   * `voorkeur` en pas daarna het andere systeem. Herkent "202C", "pms 202 c",
   * "PANTONE 202 C", "202-c", kaal "202", en aan TCX-kant "18-1535",
   * "18-1535 tpg" en "PANTONE 18-1535 TCX". Onbekende code -> null; we gokken
   * nooit een kleur.
   */
  function zoekPantone(invoer, voorkeur) {
    if (!DATA || !invoer) return null;
    var sleutel = plet(schoonPantone(invoer));
    if (!sleutel) return null;
    var eerste = herkenSysteem(invoer) || (voorkeur === 'coated' ? 'coated' : SYSTEEM_STANDAARD);
    var volgorde = eerste === 'tcx' ? ['tcx', 'coated'] : ['coated', 'tcx'];
    for (var i = 0; i < volgorde.length; i++) {
      var treffer = volgorde[i] === 'tcx' ? zoekTcx(sleutel) : zoekCoated(sleutel);
      if (treffer) return treffer;
    }
    return null;
  }

  /** De set + het achtervoegsel die bij het gekozen systeem horen. */
  function pantoneSet(systeem) {
    if (systeem === 'tcx' && DATA && DATA.tcx) return { set: DATA.tcx, suffix: DATA.tcxSuffix };
    return { set: DATA.pantone, suffix: DATA.suffix };
  }

  /**
   * De dichtstbijzijnde code in het ándere systeem. Tussen TCX en Solid Coated
   * bestaat geen officiële omrekentabel, alleen deze benadering; daarom staat de
   * ΔE er op de pagina altijd bij. Beide sets nodig, anders geen kruisverwijzing.
   */
  function kruisverwijzing(hex, systeem) {
    if (!DATA || !DATA.tcx || !DATA.pantone) return null;
    var lab = hexNaarLab(hex);
    if (!lab) return null;
    var ander = systeem === 'tcx' ? 'coated' : 'tcx';
    var doel = pantoneSet(ander);
    var treffer = dichtstbij(doel.set, lab, doel.suffix);
    if (!treffer) return null;
    return {
      system: ander,
      code: treffer.code,
      hex: treffer.hex,
      name: ander === 'tcx' ? (treffer.name || '') : '',
      deltaE: treffer.deltaE
    };
  }

  var OPSLAG_SYSTEEM = 'tr26.pantoneSystem';

  /** Opgeslagen voorkeur; privémodus of geblokkeerde opslag gooit, dan gewoon null. */
  function leesSysteemVoorkeur() {
    try {
      var waarde = window.localStorage.getItem(OPSLAG_SYSTEEM);
      return waarde === 'tcx' || waarde === 'coated' ? waarde : null;
    } catch (e) { return null; }
  }

  function bewaarSysteemVoorkeur(systeem) {
    try { window.localStorage.setItem(OPSLAG_SYSTEEM, systeem); } catch (e) { /* opslag mag de tool nooit breken */ }
  }

  var OPSLAG_WIELTYPE = 'tr26.wielType';

  /** Welk wiel de bezoeker de vorige keer koos; onbruikbare opslag geeft null. */
  function leesWielVoorkeur() {
    try {
      var waarde = window.localStorage.getItem(OPSLAG_WIELTYPE);
      return waarde === 'hex' || waarde === 'pantone' || waarde === 'ral' ? waarde : null;
    } catch (e) { return null; }
  }

  function bewaarWielVoorkeur(type) {
    try { window.localStorage.setItem(OPSLAG_WIELTYPE, type); } catch (e) { /* opslag mag de tool nooit breken */ }
  }

  /** Herkent "RAL3004", "ral 3004", "RAL 3004" en kaal "3004". Onbekend -> null. */
  function zoekRal(invoer) {
    if (!DATA || !invoer) return null;
    var cijfers = String(invoer).toLowerCase().replace(/\bral\b/g, ' ').replace(/[^0-9]/g, '');
    if (cijfers.length !== 4) return null;
    var set = DATA.ral;
    var n = set.opSleutel['k' + cijfers];
    if (n === undefined) return null;
    return { code: set.codes[n], hex: set.hexes[n], name: set.namenNl[n], nameEn: set.namenEn[n], deltaE: 0 };
  }

  /* ─── Kleurwiel: HSV-wiel en buurtkaart ──────────────────────────────── */

  /**
   * Twee soorten wiel delen dit blok.
   *
   * 1. Het HEX-wiel is een gewone kleurkiezer: tint rond, verzadiging naar
   *    buiten, lichtheid (HSV-waarde) op een aparte schuif. Alles wat het wiel
   *    laat zien bestaat, want elke schermkleur bestaat.
   * 2. De buurtkaart toont geen spectrum maar alleen echte, bestelbare codes uit
   *    één tabel (Pantone TCX, Pantone Solid Coated of RAL Classic) rond de
   *    gekozen kleur: afstand tot het midden is het kleurverschil (CIEDE2000),
   *    richting is de tintverschuiving in CIELAB. De lichtheid zit niet in die
   *    richting en krijgt daarom een eigen glyph.
   *
   * De buurtkaart is een overzetting van apps/frontend/src/lib/pantone-neighbourhood.ts
   * uit de beheer-app, met dezelfde constanten en dezelfde relaxatie, zodat de
   * kaart hier en daar dezelfde kleuren op dezelfde plek zet. Het enige verschil
   * is de dataset: die geven we mee in plaats van hem uit een systeemnaam af te
   * leiden, want RAL kent de beheer-app niet.
   *
   * Alles hieronder is puur: ongeldige invoer geeft null of een lege lijst,
   * nooit een exception en nooit een gegokte kleur.
   */

  /** Tot en met deze CIEDE2000-afstand noemen we twee kleuren visueel identiek. */
  var IDENTIEK_DELTA_E = 2;
  /** Tot en met deze afstand noemen we een kleur nog "dichtbij". */
  var DICHTBIJ_DELTA_E = 5;
  /**
   * Onder deze afstand in het a* en b*-vlak is de richting ruis: bij een neutraal
   * grijs bepaalt een afrondingsverschil in de tiende decimaal anders de hoek.
   * Zulke buren zetten we recht boven (lichter) of recht onder (donkerder).
   */
  var NEUTRALE_CHROMA = 0.5;
  /** Onder dit verschil in L* noemen we twee kleuren even licht. */
  var LICHTHEID_STAP = 1;
  /** Ringen op de kaart, in CIEDE2000-eenheden. */
  var DELTA_E_RINGEN = [2, 5, 10];
  /**
   * Standaardgrenzen van de buurt: 32 buren met plafond 10 vult de kaart bij elke
   * normale stofkleur; het minimum van 8 zorgt dat de kaart ook bij zwart en bij
   * een fel blauw niet leeg blijft, want daar ligt de dichtstbijzijnde code al
   * ruim boven de 10.
   */
  var BUURT_MAX_BUREN = 32;
  var BUURT_MAX_DELTA_E = 10;
  var BUURT_MIN_BUREN = 8;

  /** Twee decimalen is ruim genoeg voor een positie. */
  function rond2(waarde) { return Math.round(waarde * 100) / 100; }

  /** HSV van een hex: tint in graden, verzadiging en waarde als fractie. Null bij onzin. */
  function hexNaarHsv(hex) {
    var rgb = hexToRgb(hex);
    if (!rgb) return null;
    var r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var d = max - min;
    var h = 0;
    if (d !== 0) {
      if (max === r) h = 60 * (((g - b) / d) % 6);
      else if (max === g) h = 60 * ((b - r) / d + 2);
      else h = 60 * ((r - g) / d + 4);
      if (h < 0) h += 360;
    }
    return { h: h, s: max === 0 ? 0 : d / max, v: max };
  }

  /** Omgekeerde weg: HSV terug naar een hexcode. */
  function hsvNaarHex(h, s, v) {
    h = ((h % 360) + 360) % 360;
    s = Math.max(0, Math.min(1, s));
    v = Math.max(0, Math.min(1, v));
    var c = v * s;
    var x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    var m = v - c;
    var r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }
    return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
  }

  /**
   * Positie van de marker op het HEX-wiel, in dezelfde eenheid als `straal`.
   *
   * Tint 0 staat bovenaan en loopt met de klok mee, precies zoals een CSS
   * conic-gradient rendert; de afstand tot het midden is de verzadiging. De
   * lichtheid zit bewust niet in de positie: die staat op de schuif eronder.
   */
  function wielPositie(hsv, straal) {
    if (!hsv) return null;
    var hoek = radialen(hsv.h);
    var afstand = Math.max(0, Math.min(1, hsv.s)) * straal;
    return { x: rond2(straal + afstand * Math.sin(hoek)), y: rond2(straal - afstand * Math.cos(hoek)) };
  }

  /**
   * De omgekeerde weg: een punt op het wiel terug naar tint en verzadiging.
   * `dx` en `dy` zijn de afstand tot het midden, in dezelfde eenheid als `straal`.
   * Buiten de rand klemmen we op volle verzadiging in plaats van niets te doen:
   * wie net naast het wiel loslaat, bedoelt de rand.
   */
  function wielTintVerzadiging(dx, dy, straal) {
    var afstand = Math.sqrt(dx * dx + dy * dy);
    var tint = (graden(Math.atan2(dx, -dy)) + 360) % 360;
    return { h: tint, s: straal > 0 ? Math.max(0, Math.min(1, afstand / straal)) : 0 };
  }

  /**
   * De `n` dichtstbijzijnde kleuren uit een set, oplopend op kleurverschil.
   *
   * Bij een gelijke afstand wint de laagste index. Dat staat hier expliciet in de
   * vergelijkfunctie in plaats van te leunen op een stabiele Array.sort: dezelfde
   * kleur hoort in elke browser dezelfde kaart te geven.
   */
  function dichtstbijN(set, lab, n) {
    if (!set || !lab) return [];
    var rij = [];
    for (var i = 0; i < set.codes.length; i++) {
      rij.push({
        index: i,
        deltaE: deltaE2000(lab[0], lab[1], lab[2], set.labs[i * 3], set.labs[i * 3 + 1], set.labs[i * 3 + 2])
      });
    }
    rij.sort(function (a, b) { return a.deltaE === b.deltaE ? a.index - b.index : a.deltaE - b.deltaE; });
    var uit = [];
    var aantal = Math.max(0, Math.min(n, rij.length));
    for (var j = 0; j < aantal; j++) {
      var p = rij[j];
      uit.push({
        index: p.index,
        code: set.codes[p.index],
        hex: set.hexes[p.index],
        naam: set.namenNl[p.index] || '',
        deltaE: p.deltaE
      });
    }
    return uit;
  }

  /**
   * Richting van een buur in het a* en b*-vlak, in graden.
   * 0 = rechts = roder (+a*), 90 = boven = geler (+b*), 180 = groener,
   * 270 = blauwer. Dat is de gewone wiskundige richting met b* omhoog; bij het
   * tekenen draaien we y om, want op het scherm loopt y naar beneden.
   */
  function richtingGraden(deltaA, deltaB) {
    return (graden(Math.atan2(deltaB, deltaA)) + 360) % 360;
  }

  /** Lichtheid van een buur ten opzichte van de gekozen kleur. */
  function lichtheidKlasse(deltaL) {
    if (Math.abs(deltaL) < LICHTHEID_STAP) return 'gelijk';
    return deltaL > 0 ? 'lichter' : 'donkerder';
  }

  /** Klasse bij een kleurverschil; bepaalt het woord en de kleur van de badge. */
  function deltaEKlasse(deltaE) {
    if (deltaE <= IDENTIEK_DELTA_E) return 'identiek';
    if (deltaE <= DICHTBIJ_DELTA_E) return 'dichtbij';
    return 'anders';
  }

  /**
   * Verschilrichtingen van een buur, in vaste volgorde: tint eerst, lichtheid
   * daarna. Een verschil kleiner dan 1 eenheid noemen we niet: dat ziet niemand,
   * en het zou bij vrijwel elke buur "roder, geler" opleveren.
   */
  function beschrijfBuur(buur) {
    var delen = [];
    if (Math.abs(buur.deltaA) >= 1) delen.push(buur.deltaA > 0 ? 'roder' : 'groener');
    if (Math.abs(buur.deltaB) >= 1) delen.push(buur.deltaB > 0 ? 'geler' : 'blauwer');
    var licht = lichtheidKlasse(buur.deltaL);
    if (licht !== 'gelijk') delen.push(licht);
    return delen;
  }

  /**
   * Positie van een buur op een kaart met `straal` straal.
   * Wat verder weg ligt dan de schaal blijft op de buitenrand staan in plaats van
   * buiten beeld te vallen.
   */
  function buurPositie(buur, straal, schaalDeltaE, markerStraal) {
    var bereik = Math.max(0, straal - markerStraal);
    var schaal = schaalDeltaE > 0 ? schaalDeltaE : 1;
    var afstand = (Math.min(Math.max(buur.deltaE, 0), schaal) / schaal) * bereik;
    var hoek = radialen(buur.hoek);
    return { x: rond2(straal + afstand * Math.cos(hoek)), y: rond2(straal - afstand * Math.sin(hoek)) };
  }

  /** Straal van de ring die bij een kleurverschil hoort. */
  function ringStraal(deltaE, straal, schaalDeltaE, markerStraal) {
    var bereik = Math.max(0, straal - markerStraal);
    var schaal = schaalDeltaE > 0 ? schaalDeltaE : 1;
    return rond2((Math.min(Math.max(deltaE, 0), schaal) / schaal) * bereik);
  }

  /**
   * Duwt markers die op elkaar liggen net genoeg uit elkaar om ze aan te kunnen
   * tikken, zonder de kaart te vervormen.
   *
   * Het punt op index 0 (de dichtstbijzijnde kleur) blijft staan: dat is de
   * marker waar de bezoeker op afgaat, die mag niet verschuiven. Elk ander punt
   * verschuift hooguit `maxVerschuiving` in totaal, zodat richting en afstand
   * aflezbaar blijven. Punten die exact samenvallen krijgen een richting uit hun
   * index, zodat dezelfde kleur altijd dezelfde kaart geeft.
   */
  function ontwar(punten, minAfstand, maxVerschuiving, rondes) {
    var uit = [];
    for (var a = 0; a < punten.length; a++) uit.push({ x: punten[a].x, y: punten[a].y });
    if (uit.length < 2 || minAfstand <= 0 || maxVerschuiving <= 0) return uit;

    var budget = [];
    for (var b = 0; b < uit.length; b++) budget.push(b === 0 ? 0 : maxVerschuiving);
    var maxRondes = rondes === undefined ? 12 : rondes;

    for (var ronde = 0; ronde < maxRondes; ronde++) {
      var bewogen = false;
      for (var i = 0; i < uit.length; i++) {
        for (var j = i + 1; j < uit.length; j++) {
          var dx = uit[j].x - uit[i].x;
          var dy = uit[j].y - uit[i].y;
          var afstand = Math.sqrt(dx * dx + dy * dy);
          var ux, uy;
          if (afstand < 1e-6) {
            // Exact op elkaar: richting uit de index, via de gulden hoek zodat
            // opeenvolgende markers niet dezelfde kant op schieten.
            var hoek = radialen(j * 137.5);
            ux = Math.cos(hoek); uy = Math.sin(hoek);
            afstand = 0;
          } else {
            ux = dx / afstand; uy = dy / afstand;
          }
          var tekort = minAfstand - afstand;
          if (tekort <= 1e-6) continue;
          // Een vastgezet punt geeft zijn helft door aan de ander, anders komt een
          // marker naast het midden nooit meer vrij.
          var beweegbaar = (budget[i] > 0 ? 1 : 0) + (budget[j] > 0 ? 1 : 0);
          if (beweegbaar === 0) continue;
          var deel = tekort / beweegbaar;
          var paren = [[i, -1], [j, 1]];
          for (var k = 0; k < 2; k++) {
            var idx = paren[k][0], teken = paren[k][1];
            if (budget[idx] <= 0) continue;
            var stap = Math.min(deel, budget[idx]);
            if (stap <= 1e-6) continue;
            uit[idx].x += teken * ux * stap;
            uit[idx].y += teken * uy * stap;
            budget[idx] -= stap;
            bewogen = true;
          }
        }
      }
      if (!bewogen) break;
    }

    for (var c = 0; c < uit.length; c++) { uit[c].x = rond2(uit[c].x); uit[c].y = rond2(uit[c].y); }
    return uit;
  }

  /**
   * Bouwt de buurt rond een hex uit één kleurtabel.
   *
   * `suffix` is wat achter de basiscode hoort (" TCX", " C" of niets bij RAL);
   * de blob bewaart die niet mee. Geeft null bij een hex die parseHex niet
   * herkent of bij een ontbrekende tabel: de kaart blijft dan leeg in plaats van
   * een kleur te gokken.
   */
  function buurtkaart(set, suffix, hex, opties) {
    var doelHex = parseHex(hex);
    if (!set || !doelHex) return null;
    opties = opties || {};

    var maxBuren = Math.max(0, opties.maxBuren === undefined ? BUURT_MAX_BUREN : opties.maxBuren);
    var maxDeltaE = opties.maxDeltaE === undefined ? BUURT_MAX_DELTA_E : opties.maxDeltaE;
    // Het maximum wint altijd van het minimum: wie 5 buren vraagt, krijgt er 5.
    var minBuren = Math.min(maxBuren, Math.max(0, opties.minBuren === undefined ? BUURT_MIN_BUREN : opties.minBuren));

    var doel = hexNaarLab(doelHex);
    var treffers = dichtstbijN(set, doel, maxBuren);
    var buren = [];
    for (var i = 0; i < treffers.length; i++) {
      var t = treffers[i];
      if (i >= minBuren && t.deltaE > maxDeltaE) continue;
      var lab = hexNaarLab(t.hex);
      if (!lab) continue;
      var deltaL = lab[0] - doel[0];
      var deltaA = lab[1] - doel[1];
      var deltaB = lab[2] - doel[2];
      var chroma = Math.sqrt(deltaA * deltaA + deltaB * deltaB);
      var licht = lichtheidKlasse(deltaL);
      var neutraal = chroma < NEUTRALE_CHROMA;
      buren.push({
        code: t.code + (suffix || ''),
        naam: t.naam,
        hex: t.hex,
        lab: lab,
        deltaE: t.deltaE,
        deltaL: deltaL,
        deltaA: deltaA,
        deltaB: deltaB,
        chromaAfstand: chroma,
        // Bij een neutrale buur is de hoek in het a* en b*-vlak ruis; dan zegt de
        // lichtheid het enige echte verschil: boven lichter, onder donkerder.
        hoek: neutraal ? (licht === 'donkerder' ? 270 : 90) : richtingGraden(deltaA, deltaB),
        neutraleRichting: neutraal,
        lichtheid: licht,
        exact: t.deltaE <= EXACT_DELTA_E
      });
    }

    // De buitenrand rekt mee zodra de minimum-acht verder liggen dan het plafond,
    // anders plakken die acht allemaal op de rand.
    var verste = buren.length ? buren[buren.length - 1].deltaE : 0;
    return { hex: doelHex, lab: doel, buren: buren, schaalDeltaE: Math.max(maxDeltaE, verste) };
  }

  /** Verschil in L* als tekst: "+2,6" of "-2,3". Komma, net als toonDeltaE. */
  function toonVerschilL(waarde) {
    var afgerond = Math.round(waarde * 10) / 10;
    var tekst = Math.abs(afgerond).toFixed(1).replace('.', ',');
    if (afgerond > 0) return '+' + tekst;
    if (afgerond < 0) return '-' + tekst;
    return tekst;
  }

  /* ─── Foto: dominante kleuren, volledig in de browser ────────────────── */

  var FOTO_MAX_ZIJDE = 160;   // downscalen: 160px is ruim genoeg voor een kleurhistogram
  var FOTO_MAX_BYTES = 25 * 1024 * 1024;

  function dominanteKleuren(afbeelding, aantal) {
    var schaal = Math.min(1, FOTO_MAX_ZIJDE / Math.max(afbeelding.naturalWidth || 1, afbeelding.naturalHeight || 1));
    var w = Math.max(1, Math.round((afbeelding.naturalWidth || 1) * schaal));
    var h = Math.max(1, Math.round((afbeelding.naturalHeight || 1) * schaal));
    var canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return [];
    ctx.drawImage(afbeelding, 0, 0, w, h);

    var pixels;
    try { pixels = ctx.getImageData(0, 0, w, h).data; }
    catch (e) { return []; } // getekende afbeelding uit een andere origin, kan hier niet voorkomen

    // Histogram op 4 bits per kanaal (16 niveaus): 4096 emmers, ruim genoeg om
    // dominante vlakken te vinden zonder ruis van jpeg-artefacten.
    var tellingen = new Int32Array(4096);
    var somR = new Float64Array(4096), somG = new Float64Array(4096), somB = new Float64Array(4096);
    var totaal = 0;
    for (var i = 0; i < pixels.length; i += 4) {
      if (pixels[i + 3] < 200) continue;
      var r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
      var k = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
      tellingen[k]++; somR[k] += r; somG[k] += g; somB[k] += b;
      totaal++;
    }
    if (!totaal) return [];

    var kandidaten = [];
    for (var j = 0; j < 4096; j++) {
      if (!tellingen[j]) continue;
      kandidaten.push({
        hex: rgbToHex(somR[j] / tellingen[j], somG[j] / tellingen[j], somB[j] / tellingen[j]),
        deel: tellingen[j] / totaal
      });
    }
    kandidaten.sort(function (a, b) { return b.deel - a.deel; });

    // Greedy selecteren met afnemende minimumafstand, zodat je nooit zes tinten
    // van hetzelfde grijs krijgt, maar wél altijd een gevulde rij.
    var gekozen = [];
    var drempels = [16, 9, 5, 2];
    for (var d = 0; d < drempels.length && gekozen.length < aantal; d++) {
      for (var c = 0; c < kandidaten.length && gekozen.length < aantal; c++) {
        var kand = kandidaten[c];
        var botst = false;
        for (var s = 0; s < gekozen.length; s++) {
          if (gekozen[s].hex === kand.hex || deltaE2000Hex(gekozen[s].hex, kand.hex) < drempels[d]) { botst = true; break; }
        }
        if (!botst) gekozen.push(kand);
      }
    }
    return gekozen;
  }

  /* ─── Backend ────────────────────────────────────────────────────────── */

  function postJson(url, body, timeoutMs) {
    return new Promise(function (klaar, mislukt) {
      if (typeof window.fetch !== 'function') { mislukt(new Error('fetch niet beschikbaar')); return; }
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var klok = setTimeout(function () {
        if (controller) controller.abort();
        mislukt(new Error('tijdslimiet'));
      }, timeoutMs || 8000);

      var opties = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      };
      if (controller) opties.signal = controller.signal;

      window.fetch(url, opties).then(function (res) {
        clearTimeout(klok);
        if (!res.ok) {
          var fout = new Error('status ' + res.status);
          fout.status = res.status; // 429 = rate-limit, dat verdient een eigen melding
          mislukt(fout);
          return;
        }
        return res.json().then(klaar, function () { mislukt(new Error('ongeldig antwoord')); });
      }, function (e) {
        clearTimeout(klok);
        mislukt(e);
      });
    });
  }

  /* ─── Hulpjes ────────────────────────────────────────────────────────── */

  function el(root, sel) { return root.querySelector(sel); }
  function alle(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function tekst(node, waarde) { if (node) node.textContent = waarde == null ? '' : String(waarde); }

  /** De backend levert `price` als string ("9.99") of null; ouder mock-materiaal als getal. */
  function euro(waarde) {
    if (waarde == null || waarde === '') return '';
    if (typeof waarde === 'number') return '€' + waarde.toFixed(2).replace('.', ',');
    var s = String(waarde);
    if (/[€a-zA-Z]/.test(s)) return s;
    var n = parseFloat(s.replace(',', '.'));
    return isFinite(n) ? '€' + n.toFixed(2).replace('.', ',') : s;
  }

  /**
   * Zet er precies één prefix voor. De backend levert codes mét prefix
   * ("PANTONE 5585 C", "RAL 7038"), onze eigen tabel zonder ("5585 C", "7038"),
   * zonder deze controle staat er "PANTONE PANTONE 5585 C" op het scherm.
   */
  function metPrefix(code, prefix) {
    var s = String(code == null ? '' : code).trim();
    if (!s) return '';
    return s.toUpperCase().indexOf(prefix.toUpperCase() + ' ') === 0 ? s : prefix + ' ' + s;
  }

  function duw(gebeurtenis, velden) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: gebeurtenis };
      for (var k in velden) if (Object.prototype.hasOwnProperty.call(velden, k)) payload[k] = velden[k];
      window.dataLayer.push(payload);
    } catch (e) { /* analytics mag nooit de tool breken */ }
  }

  function leesbareInkt(hex) {
    var rgb = hexToRgb(hex);
    if (!rgb) return '#16130f';
    // Relatieve luminantie (WCAG): onder 0,45 witte tekst erop.
    var lum = 0.2126 * srgbNaarLineair(rgb.r) + 0.7152 * srgbNaarLineair(rgb.g) + 0.0722 * srgbNaarLineair(rgb.b);
    return lum < 0.45 ? '#ffffff' : '#16130f';
  }

  /* ─── Sectie ─────────────────────────────────────────────────────────── */

  function init(root) {
    if (root.dataset.kmKlaar === '1') return;
    root.dataset.kmKlaar = '1';

    var cfg = {};
    var cfgEl = el(root, '.km26-config');
    if (cfgEl) { try { cfg = JSON.parse(cfgEl.textContent) || {}; } catch (e) { cfg = {}; } }

    var API = String(cfg.api || '').replace(/\/+$/, '');
    var DATA_URL = cfg.dataUrl || '';
    var START_HEX = parseHex(cfg.startHex) || '#B2C5B4';

    var dom = {
      picker: el(root, '.km26-pick'),
      wielBlok: el(root, '.km26-wiel'),
      wielType: el(root, '[data-veld="wiel-type"]'),
      wielTitel: el(root, '[data-wiel="titel"]'),
      wielUitleg: el(root, '[data-wiel="uitleg"]'),
      wielVlak: el(root, '[data-wiel="vlak"]'),
      wielSliderRij: el(root, '[data-wiel="schuifrij"]'),
      wielSlider: el(root, '[data-wiel="schuif"]'),
      wielSchuifWaarde: el(root, '[data-wiel="schuifwaarde"]'),
      buurInfo: el(root, '[data-wiel="info"]'),
      buurLijst: el(root, '[data-wiel="lijst"]'),
      buurMeer: el(root, '[data-wiel="meer"]'),
      wielLegenda: el(root, '[data-wiel="legenda"]'),
      hex: el(root, '[data-veld="hex"]'),
      pantone: el(root, '[data-veld="pantone"]'),
      systeem: el(root, '[data-veld="pantone-systeem"]'),
      ral: el(root, '[data-veld="ral"]'),
      preview: el(root, '.km26-preview'),
      rijHex: el(root, '[data-code="hex"]'),
      rijPantone: el(root, '[data-code="pantone"]'),
      rijKruis: el(root, '.km26-code-kruis'),
      rijRal: el(root, '[data-code="ral"]'),
      rijCmyk: el(root, '[data-code="cmyk"]'),
      paletRij: el(root, '.km26-paletrij'),
      bestand: el(root, '.km26-bestand'),
      drop: el(root, '.km26-drop'),
      fotoRij: el(root, '.km26-fotorij'),
      fotoPrev: el(root, '.km26-fotoprev'),
      domRij: el(root, '.km26-domrij'),
      fotoMelding: el(root, '.km26-fotomelding'),
      prodStatus: el(root, '.km26-prodstatus'),
      prodInhoud: el(root, '.km26-prodinhoud'),
      combi: el(root, '.km26-combi'),
      form: el(root, '.km26-form'),
      formMelding: el(root, '.km26-formmelding'),
      leadHex: el(root, '[data-lead="hex"]'),
      leadSysteem: el(root, '[data-lead="pantoneSystem"]'),
      leadHexDot: el(root, '.km26-leadhex-dot'),
      leadHexTekst: el(root, '.km26-leadhex-tekst')
    };

    var staat = {
      hex: START_HEX,
      dataKlaar: false,
      volgnummer: 0,
      laatsteApiHex: null,
      dataFout: false,
      // Textiel is onze standaard; een eerder gemaakte keuze gaat daarvoor.
      systeem: leesSysteemVoorkeur() || SYSTEEM_STANDAARD,
      // Het wiel begint op "alle kleuren": de twee buurtkaarten hebben een
      // gekozen kleur nodig om omheen te tekenen, en de bezoeker begint bij de
      // vraag welke kleur hij wil.
      wielType: leesWielVoorkeur() || 'hex',
      wielHsv: hexNaarHsv(START_HEX),
      wielKaart: null,
      wielActief: 0,
      wielAlles: false
    };

    /* (weergave) */

    function toonPreview() {
      if (dom.preview) dom.preview.style.background = staat.hex;
      if (dom.picker) dom.picker.value = staat.hex.toLowerCase();
      if (dom.hex && document.activeElement !== dom.hex) dom.hex.value = staat.hex;
      if (dom.leadHexDot) dom.leadHexDot.style.background = staat.hex;
      if (dom.leadHexTekst) tekst(dom.leadHexTekst, staat.hex);
      if (dom.leadHex) dom.leadHex.value = staat.hex;
    }

    function zetCodeRij(rij, waarde, onder, kopieerWaarde, naam) {
      if (!rij) return;
      var v = el(rij, '.km26-code-v');
      if (!v) return;
      rij.hidden = false;
      v.innerHTML = '';
      var hoofd = document.createElement('span');
      hoofd.textContent = waarde || '-';
      v.appendChild(hoofd);
      // De kleurnaam staat naast de code, niet erin: de kopieerknop moet alleen
      // "PANTONE 18-1535 TCX" opleveren, want dat is wat je in een offerte plakt.
      if (naam) {
        // Een echte spatie ertussen, zodat code en naam gescheiden blijven bij het
        // kopiëren met de muis en in de voorleesvolgorde. Het scheidingsteken zelf
        // komt uit CSS (::before) en verdwijnt op een smal scherm, waar de naam op
        // een eigen regel valt: als tekstknooppunt stond het daar als los
        // opsommingsteken aan het begin van de tweede regel.
        v.appendChild(document.createTextNode(' '));
        var naamEl = document.createElement('span');
        naamEl.className = 'km26-code-naam';
        naamEl.textContent = naam;
        v.appendChild(naamEl);
      }
      if (onder) {
        var klein = document.createElement('small');
        klein.textContent = onder;
        v.appendChild(klein);
      }
      var knop = el(rij, '.km26-kopieer');
      if (knop) {
        knop.dataset.waarde = kopieerWaarde || waarde || '';
        knop.hidden = !(kopieerWaarde || waarde);
      }
    }

    /**
     * `wachten` = de kleurtabel is nog onderweg, dan tonen we "Wordt berekend…".
     * Is er geen match (de backend mag nearestPantone/nearestRal op null zetten),
     * dan verbergen we de regel helemaal, een leeg streepje belooft niets, maar
     * een lege regel roept ook geen vragen op.
     */
    /**
     * De code uit het andere systeem, als losse regel onder de Pantone-rij.
     * Nooit als gelijkteken presenteren: het woord "benadering" en de ΔE horen
     * er allebei bij, want een officiële omrekening bestaat niet.
     */
    function kruisTekst(kruis) {
      if (!kruis || !kruis.code) return '';
      var code = metPrefix(kruis.code, 'PANTONE');
      var naam = kruis.system === 'tcx' && kruis.name ? ' ' + kruis.name : '';
      var delta = toonDeltaE(kruis.deltaE);
      var tussen = delta === 'exact' ? 'benadering, verschil niet zichtbaar'
        : (delta ? 'benadering, ' + delta : 'benadering');
      var kop = kruis.system === 'tcx' ? 'Dichtstbijzijnde textielkleur: ' : 'Dichtstbijzijnde drukkleur: ';
      return kop + code + naam + ' (' + tussen + ')';
    }

    function toonKruis(kruis) {
      if (!dom.rijKruis) return;
      var regel = kruisTekst(kruis);
      tekst(dom.rijKruis, regel);
      dom.rijKruis.hidden = !regel;
    }

    function toonCodes(codes, wachten) {
      zetCodeRij(dom.rijHex, codes.hex, null, codes.hex);

      if (codes.pantone && codes.pantone.code) {
        var pantoneCode = metPrefix(codes.pantone.code, 'PANTONE');
        zetCodeRij(dom.rijPantone, pantoneCode, toonDeltaE(codes.pantone.deltaE), pantoneCode, codes.pantone.name || '');
      } else if (wachten) {
        zetCodeRij(dom.rijPantone, 'Wordt berekend…', null, '');
      } else if (dom.rijPantone) {
        dom.rijPantone.hidden = true;
      }
      toonKruis(codes.kruis);

      if (codes.ral && codes.ral.code) {
        var ralCode = metPrefix(codes.ral.code, 'RAL');
        var onder = [codes.ral.name || '', toonDeltaE(codes.ral.deltaE)].filter(Boolean).join(' · ');
        zetCodeRij(dom.rijRal, ralCode, onder, ralCode);
      } else if (wachten) {
        zetCodeRij(dom.rijRal, 'Wordt berekend…', null, '');
      } else if (dom.rijRal) {
        dom.rijRal.hidden = true;
      }

      if (codes.cmyk) zetCodeRij(dom.rijCmyk, 'C/M/Y/K ' + codes.cmyk, null, codes.cmyk);
      else if (dom.rijCmyk) dom.rijCmyk.hidden = true;
    }

    function toonPalet(palet) {
      if (!dom.paletRij) return;
      dom.paletRij.innerHTML = '';
      (palet || []).forEach(function (kleur) {
        var hex = parseHex(kleur.hex);
        if (!hex) return;
        var knop = document.createElement('button');
        knop.type = 'button';
        knop.className = 'km26-paletkleur';
        knop.setAttribute('aria-label', rolLabel(kleur.role) + ' ' + hex + ', gebruik deze kleur');
        var vlak = document.createElement('span');
        vlak.className = 'km26-paletvlak';
        vlak.style.background = hex;
        var rol = document.createElement('span');
        rol.className = 'km26-paletrol';
        rol.textContent = rolLabel(kleur.role);
        var code = document.createElement('span');
        code.className = 'km26-palethex';
        code.textContent = hex;
        knop.appendChild(vlak); knop.appendChild(rol); knop.appendChild(code);
        knop.addEventListener('click', function () { zetKleur(hex, 'palet'); });
        dom.paletRij.appendChild(knop);
      });
    }

    /* (kleurwiel) */

    /**
     * Het wiel is het hoofdinstrument van stap 1; de native kleurkiezer blijft
     * ernaast staan als compact alternatief voor toetsenbord en schermlezer.
     *
     * Drie soorten, één keuzeveld:
     * - "hex": een gewone kleurkiezer, tint rond en verzadiging naar buiten, met
     *   de lichtheid op een schuif eronder.
     * - "pantone" en "ral": de buurtkaart, die geen spectrum toont maar alleen
     *   codes die echt bestaan, rond de kleur die nu gekozen is.
     *
     * Het wiel is met de muis en de vinger te bedienen, niet met het toetsenbord:
     * een tint-verzadigingsvlak in twee richtingen is met pijltjestoetsen geen
     * bruikbaar instrument. De hexinvoer en de systeemkleurkiezer zijn daarvoor de
     * route, en de buurtkaart heeft naast de markers een lijst met dezelfde
     * kleuren die wél gewoon in de tabvolgorde staat.
     */

    /* Interne rekeneenheid van het wiel. De weergave schaalt mee met de kolom, dus
       alles wat we tekenen staat in procenten van deze eenheid; zo klopt de kaart
       op 420 px net zo goed als op 330 px. */
    var WIEL_EENHEID = 300;
    var WIEL_STRAAL = WIEL_EENHEID / 2;
    /* Onder deze breedte (de cirkel in echte pixels) wordt de glyph in de marker
       zo groot dat er van de kleur zelf nauwelijks iets overblijft. */
    var WIEL_KLEIN_PX = 260;
    var BUURLIJST_KORT = 8;
    var SVG_NS = 'http://www.w3.org/2000/svg';

    var hexWiel = null;   // opgebouwde DOM van het HEX-wiel, zodat slepen niets hoeft te herbouwen
    var sleepFrame = null;
    var sleepPunt = null;
    var sleept = false;

    function volgendFrame(fn) {
      if (typeof window.requestAnimationFrame === 'function') return window.requestAnimationFrame(fn);
      return setTimeout(fn, 16);
    }
    function stopFrame(id) {
      if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(id);
      else clearTimeout(id);
    }

    function svgEl(naam, kenmerken) {
      var node = document.createElementNS(SVG_NS, naam);
      for (var k in kenmerken) {
        if (Object.prototype.hasOwnProperty.call(kenmerken, k)) node.setAttribute(k, String(kenmerken[k]));
      }
      return node;
    }

    /** Percentage van de wieleenheid; markers en ringen staan zo op elke breedte goed. */
    function pct(waarde) { return (waarde / WIEL_EENHEID) * 100 + '%'; }

    function wielIsBuurt() { return staat.wielType === 'pantone' || staat.wielType === 'ral'; }

    /**
     * De kleurtabel die bij het gekozen wiel hoort, met alles wat de kaart nodig
     * heeft om er tekst van te maken. Null zolang de tabel nog niet geladen is:
     * dan toont het wiel de lege staat in plaats van een gegokte kaart.
     */
    function wielBibliotheek() {
      if (!staat.dataKlaar || !DATA) return null;
      if (staat.wielType === 'ral') {
        return { set: DATA.ral, suffix: '', prefix: 'RAL', naam: 'RAL Classic', veld: dom.ral };
      }
      if (staat.wielType === 'pantone') {
        var doel = pantoneSet(staat.systeem);
        if (!doel || !doel.set) return null;
        return {
          set: doel.set,
          suffix: doel.suffix,
          prefix: 'PANTONE',
          naam: staat.systeem === 'tcx' ? 'Pantone TCX' : 'Pantone Solid Coated',
          veld: dom.pantone
        };
      }
      return null;
    }

    /** Volledige code zoals hij op het scherm hoort: "PANTONE 18-1535 TCX", "RAL 3020". */
    function buurCode(buur, bib) { return metPrefix(buur.code, bib.prefix); }

    var LICHT_WOORD = { lichter: 'lichter', donkerder: 'donkerder', gelijk: 'even licht' };

    /** Naam van een buur voor een schermlezer en voor de tooltip. */
    function buurLabel(buur, bib) {
      var delen = [buurCode(buur, bib)];
      if (buur.naam) delen.push(buur.naam);
      var delta = toonDeltaE(buur.deltaE);
      if (delta) delen.push(delta);
      delen.push(LICHT_WOORD[buur.lichtheid]);
      return delen.join(', ');
    }

    var KLASSE_WOORD = { identiek: 'visueel identiek', dichtbij: 'dichtbij', anders: 'andere kleur' };

    /**
     * "Groener, donkerder (-2,3 L)" of "Zelfde tint, lichter (+2,6 L)".
     * Bij een neutrale buur zegt de tintrichting niets; dan is "zelfde tint" de
     * eerlijke omschrijving en doet alleen de lichtheid er nog toe.
     */
    function verschilTekst(buur) {
      var woorden;
      if (buur.neutraleRichting && buur.lichtheid !== 'gelijk') {
        woorden = ['zelfde tint', LICHT_WOORD[buur.lichtheid]];
      } else {
        var delen = beschrijfBuur(buur);
        if (!delen.length) return 'Zelfde tint, even licht';
        woorden = delen;
      }
      var zin = woorden.join(', ');
      zin = zin.charAt(0).toUpperCase() + zin.slice(1);
      if (buur.lichtheid !== 'gelijk') zin += ' (' + toonVerschilL(buur.deltaL) + ' L)';
      return zin;
    }

    function lichtGlyph(buur) {
      if (buur.lichtheid === 'lichter') return '▲';
      if (buur.lichtheid === 'donkerder') return '▼';
      return '';
    }

    /** Wit op een donkere kleur, donker op een lichte: de glyph moet leesbaar blijven. */
    function glyphInkt(lab) { return lab[0] <= 60 ? '#ffffff' : '#16130f'; }

    function staalEl(hex, klasse) {
      var s = document.createElement('span');
      s.className = klasse;
      s.style.background = hex;
      s.setAttribute('aria-hidden', 'true');
      return s;
    }

    function spanMet(klasse, inhoud) {
      var s = document.createElement('span');
      s.className = klasse;
      s.textContent = inhoud;
      return s;
    }

    /* (kop van het wielblok) */

    function wielTitelTekst() {
      if (staat.wielType === 'hex') return 'Alle kleuren';
      var bib = wielBibliotheek();
      var naam = bib ? bib.naam : (staat.wielType === 'ral' ? 'RAL Classic' : 'Pantone');
      return naam + '-kleuren rond ' + staat.hex;
    }

    function wielKop() {
      if (!dom.wielBlok) return;
      var buurt = wielIsBuurt();
      tekst(dom.wielTitel, wielTitelTekst());
      tekst(dom.wielUitleg, buurt
        ? 'Het midden is jouw kleur. De afstand tot het midden is het kleurverschil, de richting is de tintverschuiving. Tik een kleur aan voor de details.'
        : 'Klik of sleep in het wiel. De lichtheid stel je met de schuif eronder in.');
      if (dom.wielSliderRij) dom.wielSliderRij.hidden = buurt;
      if (dom.wielLegenda) dom.wielLegenda.hidden = !buurt;
      alle(root, '.km26-aslabel').forEach(function (label) { label.hidden = !buurt; });
      if (dom.wielBlok) dom.wielBlok.dataset.soort = staat.wielType;
    }

    /* (weergave) */

    function tekenWielMelding(bericht) {
      if (!dom.wielVlak) return;
      dom.wielVlak.innerHTML = '';
      dom.wielVlak.dataset.soort = 'melding';
      hexWiel = null;
      var doos = document.createElement('p');
      doos.className = 'km26-wiel-leeg';
      doos.textContent = bericht;
      dom.wielVlak.appendChild(doos);
      if (dom.buurInfo) { dom.buurInfo.innerHTML = ''; dom.buurInfo.hidden = true; delete dom.buurInfo.dataset.buur; }
      if (dom.buurLijst) { dom.buurLijst.innerHTML = ''; dom.buurLijst.hidden = true; }
      if (dom.buurMeer) dom.buurMeer.hidden = true;
    }

    /* (HEX-wiel) */

    function bouwHexWiel() {
      dom.wielVlak.innerHTML = '';
      dom.wielVlak.dataset.soort = 'hex';
      var vlak = document.createElement('div');
      vlak.className = 'km26-wiel-hex';
      vlak.setAttribute('role', 'img');
      var wit = document.createElement('span');
      wit.className = 'km26-wiel-wit';
      wit.setAttribute('aria-hidden', 'true');
      var zwart = document.createElement('span');
      zwart.className = 'km26-wiel-zwart';
      zwart.setAttribute('aria-hidden', 'true');
      var marker = document.createElement('span');
      marker.className = 'km26-wiel-marker';
      marker.setAttribute('aria-hidden', 'true');
      vlak.appendChild(wit);
      vlak.appendChild(zwart);
      vlak.appendChild(marker);
      dom.wielVlak.appendChild(vlak);
      koppelSleep(vlak);
      hexWiel = { vlak: vlak, zwart: zwart, marker: marker };
      if (dom.buurInfo) { dom.buurInfo.innerHTML = ''; dom.buurInfo.hidden = true; delete dom.buurInfo.dataset.buur; }
      if (dom.buurLijst) { dom.buurLijst.innerHTML = ''; dom.buurLijst.hidden = true; }
      if (dom.buurMeer) dom.buurMeer.hidden = true;
    }

    function werkHexWielBij() {
      if (!hexWiel) return;
      var hsv = staat.wielHsv || hexNaarHsv(staat.hex);
      if (!hsv) return;
      // De positie is de tint en de verzadiging; de lichtheid ligt als zwarte laag
      // over het hele wiel, want een plat tintvlak kent geen donkere kleuren.
      var punt = wielPositie({ h: hsv.h, s: hsv.s, v: 1 }, WIEL_STRAAL);
      hexWiel.marker.style.left = pct(punt.x);
      hexWiel.marker.style.top = pct(punt.y);
      hexWiel.marker.style.background = staat.hex;
      hexWiel.zwart.style.opacity = String(Math.max(0, Math.min(1, 1 - hsv.v)));
      hexWiel.vlak.setAttribute('aria-label',
        'Kleurwiel, tint ' + Math.round(hsv.h) + '°, verzadiging ' + Math.round(hsv.s * 100) +
        '%, lichtheid ' + Math.round(hsv.v * 100) + '%');
    }

    function zetWielSchuif(v) {
      if (dom.wielSlider) dom.wielSlider.value = String(Math.round(v * 100));
      if (dom.wielSchuifWaarde) tekst(dom.wielSchuifWaarde, Math.round(v * 100) + '%');
    }

    /** Punt op het wiel terug naar HSV; de lichtheid blijft staan waar de schuif hem zette. */
    function hsvUitPunt(vlak, clientX, clientY) {
      var vak = vlak.getBoundingClientRect();
      var straal = vak.width / 2;
      if (!(straal > 0)) return null;
      var tv = wielTintVerzadiging(clientX - (vak.left + straal), clientY - (vak.top + straal), straal);
      var v = staat.wielHsv ? staat.wielHsv.v : 1;
      return { h: tv.h, s: tv.s, v: v };
    }

    /**
     * `stil` = de bezoeker sleept nog. Dan werken we de kleur, de codes en het
     * palet gewoon bij (die lopen live mee), maar houden we de backend-aanroep en
     * het meetevent achter tot het loslaten: anders staat er per beweging een
     * verzoek en een dataLayer-regel.
     */
    function pasWielPuntToe(hsv, stil) {
      if (!hsv) return;
      staat.wielHsv = { h: hsv.h, s: hsv.s, v: hsv.v };
      zetWielSchuif(hsv.v);
      zetKleur(hsvNaarHex(hsv.h, hsv.s, hsv.v), 'wiel', stil);
      // Zwart blijft zwart bij elke tint: dan verandert staat.hex niet en tekent
      // zetKleur niets, terwijl de marker wel degelijk verplaatst is.
      werkHexWielBij();
    }

    function planWielPunt(vlak, e) {
      var hsv = hsvUitPunt(vlak, e.clientX, e.clientY);
      if (!hsv) return;
      sleepPunt = hsv;
      if (sleepFrame) return; // hooguit één berekening per animatieframe
      sleepFrame = volgendFrame(function () {
        sleepFrame = null;
        pasWielPuntToe(sleepPunt, true);
      });
    }

    function koppelSleep(vlak) {
      function loslaten(e) {
        if (!sleept) return;
        sleept = false;
        try { if (e && e.pointerId !== undefined) vlak.releasePointerCapture(e.pointerId); } catch (fout) { /* al losgelaten */ }
        window.removeEventListener('pointerup', loslaten);
        window.removeEventListener('pointercancel', loslaten);
        if (sleepFrame) { stopFrame(sleepFrame); sleepFrame = null; }
        pasWielPuntToe(sleepPunt, false);
        sleepPunt = null;
      }

      vlak.addEventListener('pointerdown', function (e) {
        // Voorkomt dat de browser er een tekstselectie of een scrollgebaar van maakt.
        e.preventDefault();
        sleept = true;
        // Met capture blijft het wiel de gebeurtenissen krijgen als de vinger
        // erbuiten schuift; zonder capture stopt het slepen bij de rand.
        try { vlak.setPointerCapture(e.pointerId); } catch (fout) { /* oudere browser: dan zonder capture */ }
        // Vangnet voor precies dat geval: zonder capture komt het loslaten búiten
        // het wiel nooit aan, en zou de kleur daarna de blote muisbeweging blijven
        // volgen zonder dat er ooit een productvoorstel wordt opgehaald.
        window.addEventListener('pointerup', loslaten);
        window.addEventListener('pointercancel', loslaten);
        planWielPunt(vlak, e);
      });
      vlak.addEventListener('pointermove', function (e) {
        if (!sleept) return;
        // Knop al los (bijvoorbeeld losgelaten buiten het venster): dan is dit geen
        // sleep meer maar een gewone muisbeweging.
        if (e.pointerType === 'mouse' && e.buttons === 0) { loslaten(e); return; }
        planWielPunt(vlak, e);
      });
      vlak.addEventListener('pointerup', loslaten);
      vlak.addEventListener('pointercancel', loslaten);
    }

    /* (buurtkaart) */

    function tekenBuurtkaart(bib) {
      var kaart = buurtkaart(bib.set, bib.suffix, staat.hex, null);
      staat.wielKaart = kaart;
      if (!kaart || !kaart.buren.length) {
        tekenWielMelding('Geen kleur in de buurt gevonden.');
        return;
      }

      /* De markerstraal hangt af van hoe breed het wiel er écht bij staat, niet van
         de rekeneenheid. Een vast percentage geeft op 375 px een stip van 13 px,
         en na een witte rand van 2 px blijft daar geen kleur van over. De beheer-app
         houdt een vloer van 8 px straal aan; die nemen we over en rekenen hem terug
         naar wieleenheden, zodat de ringen, de posities en de ontwarring met dezelfde
         marker rekenen als de knoppen die je ziet. */
      var breedtePx = dom.wielVlak.getBoundingClientRect().width || WIEL_EENHEID;
      var markerPx = Math.max(8, Math.round(breedtePx / 33));
      var markerStraal = markerPx * (WIEL_EENHEID / breedtePx);

      dom.wielVlak.innerHTML = '';
      dom.wielVlak.dataset.soort = 'buurt';
      hexWiel = null;

      var doos = document.createElement('div');
      doos.className = 'km26-wiel-kaart' + (breedtePx < WIEL_KLEIN_PX ? ' km26-wiel-kaart--klein' : '');
      doos.setAttribute('role', 'group');
      doos.setAttribute('aria-label', bib.naam + '-kleuren rond ' + kaart.hex + ', ' + kaart.buren.length + ' kleuren');

      var svg = svgEl('svg', {
        'class': 'km26-wiel-svg',
        viewBox: '0 0 ' + WIEL_EENHEID + ' ' + WIEL_EENHEID,
        'aria-hidden': 'true',
        focusable: 'false'
      });
      svg.appendChild(svgEl('circle', {
        cx: WIEL_STRAAL, cy: WIEL_STRAAL, r: WIEL_STRAAL - markerStraal,
        fill: '#faf8f3', stroke: '#efece4'
      }));
      DELTA_E_RINGEN.forEach(function (ring) {
        svg.appendChild(svgEl('circle', {
          cx: WIEL_STRAAL, cy: WIEL_STRAAL,
          r: ringStraal(ring, WIEL_STRAAL, kaart.schaalDeltaE, markerStraal),
          fill: 'none', stroke: '#ddd6cb', 'stroke-dasharray': '3 4'
        }));
      });
      svg.appendChild(svgEl('line', {
        x1: markerStraal, y1: WIEL_STRAAL, x2: WIEL_EENHEID - markerStraal, y2: WIEL_STRAAL,
        stroke: '#ddd6cb', 'stroke-width': '0.5'
      }));
      svg.appendChild(svgEl('line', {
        x1: WIEL_STRAAL, y1: markerStraal, x2: WIEL_STRAAL, y2: WIEL_EENHEID - markerStraal,
        stroke: '#ddd6cb', 'stroke-width': '0.5'
      }));
      DELTA_E_RINGEN.forEach(function (ring) {
        var straal = ringStraal(ring, WIEL_STRAAL, kaart.schaalDeltaE, markerStraal);
        var schuin = Math.SQRT1_2;
        var label = svgEl('text', {
          x: WIEL_STRAAL - straal * schuin, y: WIEL_STRAAL - straal * schuin,
          'text-anchor': 'middle', 'dominant-baseline': 'middle',
          'font-size': '10', fill: '#6f6a61', stroke: '#faf8f3', 'stroke-width': '3',
          'paint-order': 'stroke'
        });
        label.textContent = 'ΔE ' + ring;
        svg.appendChild(label);
      });
      doos.appendChild(svg);

      var midden = staalEl(kaart.hex, 'km26-wiel-midden');
      doos.appendChild(midden);

      var ruw = kaart.buren.map(function (buur) {
        return buurPositie(buur, WIEL_STRAAL, kaart.schaalDeltaE, markerStraal);
      });
      var punten = ontwar(ruw, markerStraal * 2, markerStraal * 1.5);

      kaart.buren.forEach(function (buur, i) {
        var punt = punten[i] || { x: WIEL_STRAAL, y: WIEL_STRAAL };
        var knop = document.createElement('button');
        knop.type = 'button';
        knop.className = 'km26-buur' + (buur.neutraleRichting ? ' km26-buur--neutraal' : '');
        knop.dataset.buur = String(i);
        knop.title = buurLabel(buur, bib);
        knop.setAttribute('aria-label', buurLabel(buur, bib));
        knop.style.left = pct(punt.x);
        knop.style.top = pct(punt.y);
        // Breedte in pixels, niet in procenten: alleen zo geldt de vloer van 16 px
        // ook op een smal scherm, en blijft er kleur zichtbaar naast de rand.
        knop.style.width = markerPx * 2 + 'px';
        knop.style.height = markerPx * 2 + 'px';
        knop.style.background = buur.hex;
        // De dichtstbijzijnde ligt bovenop: die wil de bezoeker als eerste raken.
        knop.style.zIndex = String(kaart.buren.length - i + 2);
        var glyph = lichtGlyph(buur);
        if (glyph) {
          var teken = spanMet('km26-buur-glyph', glyph);
          teken.setAttribute('aria-hidden', 'true');
          teken.style.color = glyphInkt(buur.lab);
          knop.appendChild(teken);
        }
        knop.addEventListener('click', function () { kiesBuur(i, bib); });
        knop.addEventListener('focus', function () { kiesBuur(i, bib); });
        knop.addEventListener('mouseenter', function () { kiesBuur(i, bib); });
        // Dubbel tikken neemt de kleur meteen over, voor wie het wiel al kent.
        knop.addEventListener('dblclick', function () { gebruikBuur(i, bib); });
        doos.appendChild(knop);
      });

      dom.wielVlak.appendChild(doos);
      // Nieuwe kaart: de strook hoort hoe dan ook opnieuw gevuld te worden, ook als
      // het indexnummer toevallig gelijk blijft.
      if (dom.buurInfo) delete dom.buurInfo.dataset.buur;
      kiesBuur(staat.wielActief && staat.wielActief < kaart.buren.length ? staat.wielActief : 0, bib);
      toonBuurlijst(bib);
    }

    /** Zet de infostrook op deze buur; de strook is een live region, dus dit spreekt. */
    function kiesBuur(i, bib) {
      var kaart = staat.wielKaart;
      if (!kaart || !kaart.buren[i]) return;
      // Aanwijzen, focussen en klikken vallen op dezelfde marker samen; zonder deze
      // uitweg bouwt de strook zich drie keer opnieuw op, spreekt de live region
      // hem drie keer uit en verliest een net gefocuste knop zijn focus.
      if (dom.buurInfo && dom.buurInfo.dataset.buur === String(i)) return;
      if (dom.buurInfo) dom.buurInfo.dataset.buur = String(i);
      staat.wielActief = i;
      alle(dom.wielVlak, '.km26-buur').forEach(function (knop, n) {
        knop.classList.toggle('is-actief', n === i);
      });
      if (dom.buurLijst) {
        alle(dom.buurLijst, '.km26-buurrij').forEach(function (rij) {
          rij.classList.toggle('is-actief', rij.dataset.buur === String(i));
        });
      }
      toonBuurInfo(kaart.buren[i], bib, i);
    }

    function toonBuurInfo(buur, bib, i) {
      if (!dom.buurInfo) return;
      dom.buurInfo.hidden = false;
      dom.buurInfo.innerHTML = '';

      var kop = document.createElement('p');
      kop.className = 'km26-buurinfo-kop';
      kop.appendChild(staalEl(buur.hex, 'km26-buurstaal'));
      kop.appendChild(spanMet('km26-buurcode', buurCode(buur, bib)));
      // Scheidingsteken vast aan de naam, zodat het bij een smalle kolom met de
      // naam meebreekt in plaats van als los teken aan het regeleinde te blijven.
      // Zelfde afspraak als in de coderij: spatie als tekstknooppunt, scheidingsteken
      // uit CSS, zodat de naam op een smal scherm zonder los opsommingsteken afbreekt.
      if (buur.naam) {
        kop.appendChild(document.createTextNode(' '));
        kop.appendChild(spanMet('km26-buurnaam', buur.naam));
      }
      kop.appendChild(document.createTextNode(' '));
      kop.appendChild(spanMet('km26-buurhex', buur.hex));
      dom.buurInfo.appendChild(kop);

      var meta = document.createElement('p');
      meta.className = 'km26-buurmeta';
      meta.appendChild(spanMet('km26-buurmeta-k', 'Kleurverschil'));
      var klasse = deltaEKlasse(buur.deltaE);
      meta.appendChild(spanMet('km26-buurbadge km26-buurbadge--' + klasse, toonDeltaE(buur.deltaE)));
      meta.appendChild(spanMet('km26-buurklasse', KLASSE_WOORD[klasse]));
      dom.buurInfo.appendChild(meta);

      var verschil = document.createElement('p');
      verschil.className = 'km26-buurverschil';
      verschil.textContent = verschilTekst(buur);
      dom.buurInfo.appendChild(verschil);

      var knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'km26-buurknop';
      knop.textContent = 'Gebruik deze kleur';
      knop.addEventListener('click', function () { gebruikBuur(i, bib); });
      dom.buurInfo.appendChild(knop);
    }

    function toonBuurlijst(bib) {
      if (!dom.buurLijst) return;
      var kaart = staat.wielKaart;
      if (!kaart) return;
      var buren = staat.wielAlles ? kaart.buren : kaart.buren.slice(0, BUURLIJST_KORT);
      dom.buurLijst.innerHTML = '';
      dom.buurLijst.hidden = false;

      // De lijst begint altijd bij de dichtstbijzijnde, dus de index in de lijst
      // is dezelfde als die in de kaart.
      buren.forEach(function (buur, i) {
        var li = document.createElement('li');
        var rij = document.createElement('button');
        rij.type = 'button';
        rij.className = 'km26-buurrij' + (i === staat.wielActief ? ' is-actief' : '');
        rij.dataset.buur = String(i);
        rij.setAttribute('aria-label', buurLabel(buur, bib) + ', toon details');
        rij.appendChild(staalEl(buur.hex, 'km26-buurstaal'));
        rij.appendChild(spanMet('km26-buurcode', buurCode(buur, bib)));
        if (buur.naam) rij.appendChild(spanMet('km26-buurnaam', buur.naam));
        var rechts = document.createElement('span');
        rechts.className = 'km26-buurrij-r';
        var glyph = lichtGlyph(buur);
        rechts.appendChild(spanMet('km26-buurlicht', (glyph ? glyph + ' ' : '') + toonVerschilL(buur.deltaL)));
        rechts.appendChild(spanMet('km26-buurbadge km26-buurbadge--' + deltaEKlasse(buur.deltaE), toonDeltaE(buur.deltaE)));
        rij.appendChild(rechts);
        rij.addEventListener('click', function () { kiesBuur(i, bib); });
        li.appendChild(rij);
        dom.buurLijst.appendChild(li);
      });

      if (dom.buurMeer) {
        var meer = kaart.buren.length > BUURLIJST_KORT;
        dom.buurMeer.hidden = !meer;
        if (meer) {
          dom.buurMeer.textContent = staat.wielAlles ? 'Toon minder' : 'Toon alle ' + kaart.buren.length;
          dom.buurMeer.setAttribute('aria-expanded', staat.wielAlles ? 'true' : 'false');
        }
      }
    }

    /**
     * Neemt de kleur van een buur over. Het invoerveld krijgt de code erbij, zodat
     * stap 2 die code exact toont en zichtbaar is waar de kleur vandaan komt. De
     * kaart tekent daarna opnieuw rond de nieuwe kleur; dat "hoppen" door de
     * waaier is precies de bedoeling.
     */
    function gebruikBuur(i, bib) {
      var kaart = staat.wielKaart;
      if (!kaart || !kaart.buren[i]) return;
      var buur = kaart.buren[i];
      if (bib.veld) {
        bib.veld.value = bib.prefix === 'RAL' ? 'RAL ' + buur.code : buur.code;
        veldMelding(bib.veld, '');
      }
      // De kaart begint na het hoppen weer bovenaan: de nieuwe kleur is nu het
      // midden, dus de oude index wijst een andere kleur aan.
      staat.wielActief = 0;
      staat.wielAlles = false;
      // Dezelfde kleur nogmaals kiezen mag hier geen no-op zijn: de bezoeker heeft
      // net een code gekozen en verwacht dat stap 2 en de kaart daarop reageren.
      staat.laatsteApiHex = null;
      zetKleur(buur.hex, 'wiel');
    }

    /* (aansturing) */

    function wielHertekenen() {
      if (!dom.wielVlak) return;
      wielKop();
      if (!wielIsBuurt()) {
        if (!hexWiel) bouwHexWiel();
        werkHexWielBij();
        return;
      }
      if (staat.dataFout || !DATA_URL) {
        tekenWielMelding('De kleurtabel is niet beschikbaar; gebruik het wiel Alle kleuren.');
        return;
      }
      var bib = wielBibliotheek();
      if (!bib) { tekenWielMelding('Kleurtabel wordt geladen…'); return; }
      tekenBuurtkaart(bib);
    }

    /**
     * Elke kleurwijziging tekent het wiel opnieuw. Komt de kleur van buiten het
     * wiel, dan leiden we de HSV opnieuw af uit de hex; komt hij uit het wiel
     * zelf, dan blijft `staat.wielHsv` de bron. Dat laatste is nodig omdat zwart
     * en grijs geen tint in de hex bewaren: de marker zou naar het midden
     * springen terwijl de bezoeker hem vasthoudt.
     */
    function wielVolgKleur(bron) {
      if (!dom.wielVlak) return;
      if (bron !== 'wiel') {
        var hsv = hexNaarHsv(staat.hex);
        if (hsv) { staat.wielHsv = hsv; zetWielSchuif(hsv.v); }
        staat.wielActief = 0;
      }
      wielHertekenen();
    }

    function zetWielType(nieuw, onthoud) {
      var type = nieuw === 'pantone' || nieuw === 'ral' ? nieuw : 'hex';
      staat.wielType = type;
      staat.wielAlles = false;
      staat.wielActief = 0;
      if (dom.wielType && dom.wielType.value !== type) dom.wielType.value = type;
      if (onthoud) bewaarWielVoorkeur(type);
      // De buurtkaart kan niets tonen zonder de kleurtabel, dus die halen we hier
      // meteen op in plaats van te wachten tot de bezoeker iets invult.
      if (type !== 'hex') zorgVoorData();
      wielHertekenen();
    }

    if (dom.wielType) {
      dom.wielType.addEventListener('change', function () {
        zetWielType(dom.wielType.value, true);
        duw('kleur_match_wiel', { wiel_type: staat.wielType, pantone_system: staat.systeem });
      });
    }

    function pasSchuifToe(stil) {
      if (!dom.wielSlider) return;
      var ingesteld = parseInt(dom.wielSlider.value, 10);
      var v = Math.max(0, Math.min(100, isFinite(ingesteld) ? ingesteld : 100)) / 100;
      var hsv = staat.wielHsv || hexNaarHsv(staat.hex) || { h: 0, s: 0, v: 1 };
      pasWielPuntToe({ h: hsv.h, s: hsv.s, v: v }, stil);
    }

    if (dom.wielSlider) {
      dom.wielSlider.addEventListener('input', function () { pasSchuifToe(true); });
      dom.wielSlider.addEventListener('change', function () { pasSchuifToe(false); });
    }

    if (dom.buurMeer) {
      dom.buurMeer.addEventListener('click', function () {
        staat.wielAlles = !staat.wielAlles;
        var bib = wielBibliotheek();
        if (bib) toonBuurlijst(bib);
      });
    }

    function statusBlok(titel, tekstInhoud, skelet) {
      if (!dom.prodStatus) return;
      dom.prodStatus.innerHTML = '';
      dom.prodStatus.hidden = false;
      if (dom.prodInhoud) dom.prodInhoud.hidden = true;
      if (skelet) {
        var s = document.createElement('div');
        s.className = 'km26-skelet';
        s.setAttribute('aria-hidden', 'true');
        for (var i = 0; i < 3; i++) s.appendChild(document.createElement('div'));
        dom.prodStatus.appendChild(s);
        var live = document.createElement('p');
        live.className = 'km26-klein';
        live.textContent = titel;
        dom.prodStatus.appendChild(live);
        return;
      }
      var doos = document.createElement('div');
      doos.className = 'km26-status';
      var kop = document.createElement('strong');
      kop.textContent = titel;
      doos.appendChild(kop);
      doos.appendChild(document.createTextNode(tekstInhoud));
      dom.prodStatus.appendChild(doos);
    }

    /*: matchkwaliteit: is het dichtstbijzijnde item écht een kleurmatch?
     *
     * De backend geeft altijd de top-3 op kleurafstand terug, hoe ver die ook af ligt.
     * Zonder deze weging presenteert de pagina een witte topper bij sage (ΔE 16,8) als
     * voorstel. `matchInfo` zegt per categorie of het beste item binnen de drempel valt;
     * ontbreekt dat veld, dan leiden we het zelf af uit de ΔE van de items.
     */

    var DREMPEL_STANDAARD = 10; // gelijk aan de backend-keuze; alleen nodig zonder matchInfo
    var CAT_SLEUTELS = ['rokken', 'toppers', 'accessoires'];

    function besteDeltaE(lijst) {
      var beste = Infinity;
      (lijst || []).forEach(function (p) {
        if (p && typeof p.deltaE === 'number' && isFinite(p.deltaE) && p.deltaE < beste) beste = p.deltaE;
      });
      return beste;
    }

    /**
     * Normaliseert het antwoord tot { drempel, rokken, toppers, accessoires } met booleans.
     * Levert de server een expliciete `hasMatch`, dan is die leidend, ook als onze eigen
     * ΔE-afleiding er anders over zou denken; de backend kent de voorraad en de drempel.
     * Staat er geen bruikbare ΔE in de items, dan kunnen we een match niet weerleggen en
     * houden we de gewone presentatie aan in plaats van er ten onrechte "geen match" van
     * te maken.
     */
    function leesMatchInfo(matchInfo, producten) {
      var drempel = DREMPEL_STANDAARD;
      if (matchInfo && typeof matchInfo.threshold === 'number' && isFinite(matchInfo.threshold) && matchInfo.threshold > 0) {
        drempel = matchInfo.threshold;
      }
      var uit = { drempel: drempel };
      CAT_SLEUTELS.forEach(function (sleutel) {
        var lijst = producten && producten[sleutel];
        if (!lijst || !lijst.length) { uit[sleutel] = false; return; }
        var vanServer = matchInfo && matchInfo[sleutel];
        if (vanServer && typeof vanServer.hasMatch === 'boolean') { uit[sleutel] = vanServer.hasMatch; return; }
        var beste = besteDeltaE(lijst);
        uit[sleutel] = isFinite(beste) ? beste <= drempel : true;
      });
      return uit;
    }

    function productKaart(p, alternatief) {
      var kaart = document.createElement('a');
      kaart.className = alternatief ? 'km26-prod km26-prod--alt' : 'km26-prod';
      kaart.href = p.url || '#';
      if (p.imageUrl) {
        var img = document.createElement('img');
        img.className = 'km26-prod-img';
        img.src = p.imageUrl;
        img.alt = p.title || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        kaart.appendChild(img);
      } else {
        var leeg = document.createElement('div');
        leeg.className = 'km26-prod-img';
        kaart.appendChild(leeg);
      }
      var body = document.createElement('div');
      body.className = 'km26-prod-b';

      // Label per kaart, zodat een alternatief ook los van zijn blok, bij scrollen,
      // in een screenreader, op een gedeelde schermafbeelding, nooit als match leest.
      if (alternatief) {
        var label = document.createElement('span');
        label.className = 'km26-prod-label';
        label.textContent = 'Andere kleur';
        body.appendChild(label);
      }

      var titel = document.createElement('div');
      titel.className = 'km26-prod-t';
      titel.textContent = p.title || p.sku || 'Product';
      body.appendChild(titel);

      if (p.colorName || p.colorHex) {
        var kleurRij = document.createElement('div');
        kleurRij.className = 'km26-prod-kleur';
        if (p.colorHex) {
          var dot = document.createElement('span');
          // Metallic: de hex is de matte basistoon, de streep uit head-2026nl doet de glans.
          // colorPantone zit nog niet in elk antwoord van de catalogus, vandaar de || ''.
          dot.className = 'km26-prod-dot' + (isMetallicPantone(p.colorPantone || '') ? ' tr26-metallic' : '');
          dot.style.background = p.colorHex;
          kleurRij.appendChild(dot);
        }
        kleurRij.appendChild(document.createTextNode(p.colorName || p.colorHex));
        body.appendChild(kleurRij);
      }

      var rij = document.createElement('div');
      rij.className = 'km26-prod-r';
      // price mag null zijn; dan laten we het prijsvak weg in plaats van een leeg vlak.
      var prijsTekst = euro(p.price);
      if (prijsTekst) {
        var prijs = document.createElement('span');
        prijs.className = 'km26-prod-p';
        prijs.textContent = prijsTekst;
        rij.appendChild(prijs);
      }
      if (typeof p.deltaE === 'number' && isFinite(p.deltaE)) {
        var badge = document.createElement('span');
        // In een alternatievenblok nooit "Zeer dichtbij", ook niet als de ΔE laag oogt:
        // de categorie is daar juist als niet-passend beoordeeld.
        var dichtbij = !alternatief && p.deltaE <= 2;
        badge.className = 'km26-badge' + (dichtbij ? ' km26-badge--exact' : '') + (alternatief ? ' km26-badge--alt' : '');
        badge.textContent = dichtbij ? 'Zeer dichtbij' : toonDeltaE(p.deltaE);
        badge.title = alternatief
          ? 'Kleurafstand tot jouw kleur (CIEDE2000): hoe hoger het getal, hoe groter het verschil'
          : 'Kleurafstand tot jouw kleur (CIEDE2000)';
        rij.appendChild(badge);
      }
      body.appendChild(rij);
      kaart.appendChild(body);
      return kaart;
    }

    var CATEGORIEEN = [
      {
        sleutel: 'rokken',
        titel: 'Statafelrokken in jouw kleur',
        sub: 'Elastische rok, past op statafels met een omtrek van 80–85 cm.',
        altKop: 'Geen statafelrok in een passende kleur gevonden',
        altSub: 'Deze rokken liggen het dichtst bij jouw kleur, maar het verschil is met het blote oog goed te zien.'
      },
      {
        sleutel: 'toppers',
        titel: 'Bijpassende statafeltoppers',
        sub: 'De topper ligt over de rok en maakt de set af.',
        altKop: 'Geen statafeltopper in een passende kleur gevonden',
        altSub: 'Deze toppers liggen het dichtst bij jouw kleur, maar het verschil is met het blote oog goed te zien.'
      },
      {
        sleutel: 'accessoires',
        titel: 'Accessoires',
        sub: 'Kleine dingen die het geheel afmaken.',
        altKop: 'Geen accessoire in een passende kleur gevonden',
        altSub: 'Deze accessoires liggen het dichtst bij jouw kleur, maar het verschil is met het blote oog goed te zien.'
      }
    ];

    function catKop(titelTekst, subTekst) {
      var kop = document.createElement('div');
      kop.className = 'km26-cat-h';
      var h3 = document.createElement('h3');
      h3.className = 'km26-h3';
      h3.textContent = titelTekst;
      var sub = document.createElement('span');
      sub.className = 'km26-klein';
      sub.style.margin = '0';
      sub.textContent = subTekst;
      kop.appendChild(h3); kop.appendChild(sub);
      return kop;
    }

    /** Blok met een echte kleurmatch: kop, ondertitel, drie kaarten. */
    function matchBlok(cat, lijst) {
      var blok = document.createElement('div');
      blok.className = 'km26-cat';
      blok.appendChild(catKop(cat.titel, cat.sub));
      var grid = document.createElement('div');
      grid.className = 'km26-grid3';
      lijst.slice(0, 3).forEach(function (p) { grid.appendChild(productKaart(p, false)); });
      blok.appendChild(grid);
      return blok;
    }

    /**
     * Blok zonder kleurmatch: eerst de melding dát er niets past, dan pas de kaarten
     * onder een eigen kopje. De kaarten staan er nog wel, ze zijn het dichtstbijzijnde
     * dat we hebben: maar in een omkaderd blok, met een label per kaart.
     */
    function alternatiefBlok(cat, lijst) {
      var blok = document.createElement('div');
      blok.className = 'km26-cat km26-cat--alt';

      var melding = document.createElement('div');
      melding.className = 'km26-altmelding';
      var kop = document.createElement('strong');
      kop.textContent = cat.altKop;
      melding.appendChild(kop);
      melding.appendChild(document.createTextNode(
        'Onze voorraad komt in ' + staat.hex + ' niet dicht genoeg in de buurt om dit een match te noemen. ' +
        'Vraag hieronder je kleurrapport aan; dan kijken we met een menselijk oog mee en sturen we een gratis stofstaal.'
      ));
      blok.appendChild(melding);

      blok.appendChild(catKop('Alternatieven in andere kleuren', cat.altSub));

      var grid = document.createElement('div');
      grid.className = 'km26-grid3';
      lijst.slice(0, 3).forEach(function (p) { grid.appendChild(productKaart(p, true)); });
      blok.appendChild(grid);
      return blok;
    }

    function toonProducten(producten, kwaliteit) {
      if (!dom.prodInhoud) return;

      dom.prodInhoud.innerHTML = '';
      var iets = false;

      CATEGORIEEN.forEach(function (cat) {
        var lijst = producten && producten[cat.sleutel];
        if (!lijst || !lijst.length) return; // geen items: blok blijft weg, zoals altijd
        iets = true;
        dom.prodInhoud.appendChild(
          kwaliteit && kwaliteit[cat.sleutel] === false ? alternatiefBlok(cat, lijst) : matchBlok(cat, lijst)
        );
      });

      if (!iets) {
        statusBlok('Nog geen match gevonden',
          'We hebben in deze kleur geen voorraad kunnen vinden. Vraag hieronder je kleurrapport aan; dan zoeken we handmatig mee en laten we weten wat er wél dicht in de buurt komt.');
        toonCombi(null, null);
        return;
      }

      dom.prodStatus.hidden = true;
      dom.prodInhoud.hidden = false;

      // De combi-kaart is een aanbeveling, geen catalogus: die tonen we alleen voor
      // categorieën die écht matchen. Matcht de rok niet, dan verdwijnt de kaart; matcht
      // de topper niet, dan blijft alleen de rok staan in plaats van een valse set.
      var rokOk = !kwaliteit || kwaliteit.rokken !== false;
      var topperOk = !kwaliteit || kwaliteit.toppers !== false;
      toonCombi(
        rokOk ? (producten.rokken && producten.rokken[0]) : null,
        rokOk && topperOk ? (producten.toppers && producten.toppers[0]) : null
      );
    }

    function toonCombi(rok, topper) {
      if (!dom.combi) return;
      if (!rok) { dom.combi.hidden = true; return; }
      dom.combi.hidden = false;
      var kicker = el(dom.combi, '.km26-combi-k');
      var titel = el(dom.combi, '.km26-combi-t');
      var sub = el(dom.combi, '.km26-combi-s');
      var dots = el(dom.combi, '.km26-combi-dots');
      var knop = el(dom.combi, '.km26-combi-knop');

      var namen = [rok.colorName || rok.title];
      if (topper) namen.push(topper.colorName || topper.title);
      // Zonder passende topper is dit geen complete set; dan belooft de kicker dat ook niet.
      tekst(kicker, topper ? 'Compleet voorstel' : 'Dichtstbijzijnde rok');
      tekst(titel, topper ? 'Rok + topper in jouw kleur' : 'Rok in jouw kleur');
      tekst(sub, topper
        ? 'Onze dichtstbijzijnde combinatie: ' + namen[0] + ' met een topper in ' + namen[1] + '. Samen vormen ze de complete set voor één statafel.'
        : 'Onze dichtstbijzijnde rok: ' + namen[0] + '. Bekijk de kleur op de productpagina.');

      if (dots) {
        dots.innerHTML = '';
        [rok, topper].forEach(function (p) {
          if (!p) return;
          var d = document.createElement('span');
          d.className = 'km26-combi-dot';
          if (p.imageUrl) d.style.backgroundImage = 'url("' + p.imageUrl + '")';
          else if (p.colorHex) {
            d.style.background = p.colorHex;
            // Alleen op een kleurvlak een glansstreep: staat er een productfoto in, dan
            // toont die de glans zelf. colorPantone kan ontbreken in het antwoord.
            if (isMetallicPantone(p.colorPantone || '')) d.className += ' tr26-metallic';
          }
          dots.appendChild(d);
        });
      }
      if (knop) knop.href = rok.url || '#';
    }

    /* (kern: kleur zetten) */

    var klokApi = null;

    /** Dichtstbijzijnde Pantone in het gekozen systeem; null zolang de tabel er niet is. */
    function lokalePantone(hex) {
      if (!staat.dataKlaar) return null;
      var lab = hexNaarLab(hex);
      if (!lab) return null;
      var doel = pantoneSet(staat.systeem);
      var treffer = dichtstbij(doel.set, lab, doel.suffix);
      if (treffer) treffer.system = staat.systeem;
      return treffer;
    }

    function berekenLokaal() {
      var codes = { hex: staat.hex, cmyk: hexNaarCmyk(staat.hex), pantone: null, ral: null, kruis: null };
      if (staat.dataKlaar) {
        var lab = hexNaarLab(staat.hex);
        codes.pantone = lokalePantone(staat.hex);
        codes.ral = dichtstbij(DATA.ral, lab, '');
        codes.kruis = kruisverwijzing(staat.hex, staat.systeem);
      }
      toonCodes(codes, !staat.dataKlaar);
      toonPalet(bouwPalet(staat.hex));
    }

    function vraagBackend() {
      if (!API) {
        statusBlok('Productvoorstel nog niet beschikbaar',
          'De koppeling met onze voorraad staat op deze pagina nog uit. Je codes en palet hierboven kloppen wél. Vraag hieronder je kleurrapport aan, dan sturen we het voorstel per e-mail.');
        toonCombi(null, null);
        return;
      }
      var mijnVolg = ++staat.volgnummer;
      var gevraagdeHex = staat.hex;
      statusBlok('We zoeken producten in ' + gevraagdeHex + '…', '', true);

      postJson(API + '/api/public/color-tool/match', { hex: gevraagdeHex, pantoneSystem: staat.systeem }, 8000).then(function (antwoord) {
        if (mijnVolg !== staat.volgnummer || gevraagdeHex !== staat.hex) return; // verouderd antwoord
        staat.laatsteApiHex = gevraagdeHex;

        // Serverantwoord is leidend voor de codes zodra het binnen is.
        if (antwoord && antwoord.input) {
          var inv = antwoord.input;
          var hexUit = parseHex(inv.hex) || gevraagdeHex;
          var serverPantone = inv.nearestPantone || null;
          // Een backend van vóór 25-08-2026 kent `system` niet en rekende in
          // Solid Coated. Wijkt het systeem van de server af van dat van de
          // pagina, dan houden we onze eigen code aan: liever geen code van de
          // server dan een zichtbare sprong tussen twee Pantone-families. De
          // rest van het antwoord (hex, RAL, CMYK, palet, producten) blijft leidend.
          var serverSysteem = serverPantone && serverPantone.system
            ? String(serverPantone.system).toLowerCase() : 'coated';
          var wachtenNu = false;
          if (!serverPantone || serverSysteem !== staat.systeem) {
            serverPantone = lokalePantone(hexUit);
            wachtenNu = !serverPantone;
          }
          toonCodes({
            hex: hexUit,
            pantone: serverPantone,
            ral: inv.nearestRal || null,
            cmyk: inv.cmyk || hexNaarCmyk(gevraagdeHex),
            // Kruisverwijzing rekenen we altijd zelf: de API levert er geen.
            kruis: kruisverwijzing(hexUit, staat.systeem)
          }, wachtenNu);
        }
        if (antwoord && antwoord.palette && antwoord.palette.length) toonPalet(antwoord.palette);
        var kwaliteit = leesMatchInfo(antwoord && antwoord.matchInfo, antwoord && antwoord.products);
        toonProducten(antwoord && antwoord.products, kwaliteit);
        duw('kleur_match_resultaat', {
          kleur_hex: gevraagdeHex,
          pantone_system: staat.systeem,
          match_rokken: kwaliteit.rokken,
          match_toppers: kwaliteit.toppers,
          match_drempel: kwaliteit.drempel
        });
      }, function () {
        if (mijnVolg !== staat.volgnummer || gevraagdeHex !== staat.hex) return;
        statusBlok('Productvoorstel even niet beschikbaar',
          'We konden onze voorraad nu niet bereiken. Je kleurcodes en palet hierboven zijn gewoon berekend. Probeer het zo nog eens, of vraag hieronder je kleurrapport aan, dan sturen we het voorstel per e-mail.');
        toonCombi(null, null);
      });
    }

    function planBackend() {
      if (klokApi) clearTimeout(klokApi);
      klokApi = setTimeout(vraagBackend, 400);
    }

    /**
     * Wisselt tussen TCX (textiel) en Solid Coated (druk).
     *
     * `onthoud` scheidt de twee manieren waarop dit gebeurt: een handmatige keuze
     * in het keuzeveld leggen we vast, automatisch meeschakelen omdat iemand een
     * code uit het andere systeem plakt niet. Anders overschrijft één geplakte
     * code de voorkeur die de bezoeker bewust had gezet.
     */
    function zetSysteem(nieuw, onthoud, stil) {
      var systeem = nieuw === 'coated' ? 'coated' : 'tcx';
      // Oud asset zonder TCX-blob: alles valt terug op Solid Coated.
      if (DATA && !DATA.tcx) systeem = 'coated';
      var gewijzigd = systeem !== staat.systeem;
      staat.systeem = systeem;
      if (dom.systeem && dom.systeem.value !== systeem) dom.systeem.value = systeem;
      if (dom.pantone) dom.pantone.placeholder = systeem === 'tcx' ? '18-1535 TCX' : '202 C';
      if (dom.leadSysteem) dom.leadSysteem.value = systeem;
      if (onthoud) bewaarSysteemVoorkeur(systeem);
      // `stil` = de aanroeper herberekent zelf meteen daarna (zie verwerkCode);
      // zonder die uitweg loopt er twee keer een volledige sweep over beide tabellen.
      if (!gewijzigd || stil) return;
      berekenLokaal();
      // Het Pantone-wiel toont de gekozen bibliotheek, dus dat hertekent mee.
      staat.wielActief = 0;
      wielHertekenen();
      // Client en server moeten in hetzelfde systeem rekenen, anders springt de
      // code alsnog zodra het serverantwoord binnenkomt.
      if (API) planBackend();
    }

    /** Ontbreekt de TCX-set, dan is er niets te kiezen en verdwijnt het keuzeveld. */
    function pasKeuzeAanOpData() {
      if (!DATA || DATA.tcx) return;
      zetSysteem('coated', false);
      if (dom.systeem) { dom.systeem.disabled = true; dom.systeem.hidden = true; }
    }

    /**
     * `stil` = de bezoeker sleept nog in het wiel. Kleur, codes en palet lopen dan
     * gewoon mee, maar de backend-aanroep en het meetevent wachten tot het
     * loslaten: anders staat er per bewogen pixel een verzoek en een dataLayer-regel.
     */
    function zetKleur(hex, bron, stil) {
      var p = parseHex(hex);
      if (!p) return;
      // Dezelfde kleur nogmaals kiezen mag geen no-op zijn zolang we er nog geen
      // productvoorstel voor hebben: de startkleur staat immers al in de kiezer,
      // en juist die wilde de bezoeker aanklikken.
      if (p === staat.hex && staat.laatsteApiHex === p) { toonPreview(); wielVolgKleur(bron); return; }
      staat.hex = p;
      toonPreview();
      berekenLokaal();
      zorgVoorData(); // codes zijn compleet zodra de dataset er is
      wielVolgKleur(bron);

      if (stil) return;
      planBackend();
      duw('kleur_match_invoer', { kleur_hex: p, kleur_bron: bron || 'onbekend', pantone_system: staat.systeem });
    }

    function zorgVoorData() {
      if (staat.dataKlaar || !DATA_URL) return;
      laadData(DATA_URL).then(function () {
        staat.dataKlaar = true;
        staat.dataFout = false;
        pasKeuzeAanOpData();
        berekenLokaal();
        wielHertekenen();
      }, function () {
        staat.dataKlaar = false;
        staat.dataFout = true;
        zetCodeRij(dom.rijPantone, 'Niet beschikbaar', 'De kleurtabel kon niet geladen worden', '');
        toonKruis(null);
        zetCodeRij(dom.rijRal, 'Niet beschikbaar', 'De kleurtabel kon niet geladen worden', '');
        wielHertekenen();
      });
    }

    /* (invoerroutes) */

    if (dom.picker) {
      dom.picker.addEventListener('input', function () { zetKleur(dom.picker.value, 'kiezer'); });
      dom.picker.addEventListener('change', function () { zetKleur(dom.picker.value, 'kiezer'); });
    }

    function veldMelding(veld, bericht) {
      var doos = veld.closest('.km26-field');
      var melding = doos ? el(doos, '.km26-melding') : null;
      if (melding) tekst(melding, bericht || '');
      veld.classList.toggle('km26-veldfout', !!bericht);
      veld.setAttribute('aria-invalid', bericht ? 'true' : 'false');
    }

    function verwerkHex() {
      var p = parseHex(dom.hex.value);
      if (!p) {
        veldMelding(dom.hex, dom.hex.value.trim() ? 'Dit lijkt geen hexcode. Voorbeeld: #B2C5B4.' : '');
        return;
      }
      veldMelding(dom.hex, '');
      zetKleur(p, 'hex');
    }

    function verwerkCode(veld, zoeker, soort) {
      var waarde = veld.value.trim();
      if (!waarde) { veldMelding(veld, ''); return; }
      if (!DATA_URL) {
        veldMelding(veld, 'De kleurtabel is op deze pagina niet beschikbaar. Gebruik zolang de hexcode of de kleurkiezer.');
        return;
      }
      zorgVoorData();
      laadData(DATA_URL).then(function () {
        staat.dataKlaar = true;
        staat.dataFout = false;
        pasKeuzeAanOpData();
        wielHertekenen();
        var treffer = zoeker(waarde);
        if (!treffer) {
          veldMelding(veld, soort === 'pantone'
            ? 'Deze Pantone-code staat niet in onze tabellen (TCX voor textiel, Solid Coated voor druk). Controleer de code of gebruik de hexcode.'
            : 'Deze RAL-code kennen we niet. RAL Classic bestaat uit vier cijfers, bijvoorbeeld 5010.');
          return;
        }
        veldMelding(veld, '');
        // Een code uit het andere systeem schakelt de keuze mee, zonder de
        // opgeslagen voorkeur te overschrijven. Stil, want zetKleur hieronder
        // berekent en bevraagt hierna toch alles opnieuw; dat scheelt een
        // tweede ΔE-sweep. `laatsteApiHex` vervalt daarbij: het serverantwoord
        // dat we hebben is in het andere systeem gerekend, dus zetKleur mag hier
        // nooit afhaken op "deze kleur hebben we al".
        if (soort === 'pantone' && treffer.system && treffer.system !== staat.systeem) {
          staat.laatsteApiHex = null;
          zetSysteem(treffer.system, false, true);
        }
        veld.value = soort === 'pantone' ? treffer.code : 'RAL ' + treffer.code;
        zetKleur(treffer.hex, soort);
      }, function () {
        staat.dataFout = true;
        wielHertekenen();
        veldMelding(veld, 'De kleurtabel kon niet geladen worden. Probeer het later opnieuw of gebruik de hexcode.');
      });
    }

    if (dom.hex) {
      dom.hex.addEventListener('change', verwerkHex);
      dom.hex.addEventListener('blur', verwerkHex);
      dom.hex.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); verwerkHex(); } });
    }
    // De gekozen familie is alleen de voorkeur: verraadt de vorm van de code het
    // systeem, dan wint die (zie zoekPantone).
    function zoekPantoneMetVoorkeur(waarde) { return zoekPantone(waarde, staat.systeem); }

    if (dom.pantone) {
      dom.pantone.addEventListener('change', function () { verwerkCode(dom.pantone, zoekPantoneMetVoorkeur, 'pantone'); });
      dom.pantone.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); verwerkCode(dom.pantone, zoekPantoneMetVoorkeur, 'pantone'); } });
    }
    if (dom.systeem) {
      dom.systeem.addEventListener('change', function () {
        zetSysteem(dom.systeem.value, true);
        duw('kleur_match_systeem', { kleur_hex: staat.hex, pantone_system: staat.systeem });
      });
    }
    if (dom.ral) {
      dom.ral.addEventListener('change', function () { verwerkCode(dom.ral, zoekRal, 'ral'); });
      dom.ral.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); verwerkCode(dom.ral, zoekRal, 'ral'); } });
    }

    // Dataset alvast laden zodra iemand het invoerblok aanraakt; dan is de tabel
    // er meestal al voordat de eerste kleur gekozen is.
    var invoerBlok = el(root, '.km26-invoer');
    if (invoerBlok) {
      ['focusin', 'pointerdown'].forEach(function (naam) {
        invoerBlok.addEventListener(naam, function eenmalig() {
          invoerBlok.removeEventListener(naam, eenmalig);
          zorgVoorData();
        });
      });
    }

    /* (foto-upload) */

    function fotoMelding(bericht, isFout) {
      if (!dom.fotoMelding) return;
      tekst(dom.fotoMelding, bericht || '');
      dom.fotoMelding.style.color = isFout ? '#b3402f' : '#6f6a61';
    }

    function verwerkBestand(bestand) {
      if (!bestand) return;
      if (!/^image\//.test(bestand.type || '')) {
        fotoMelding('Dit is geen afbeelding. Kies een JPG, PNG, WebP of HEIC-foto.', true);
        return;
      }
      if (bestand.size > FOTO_MAX_BYTES) {
        fotoMelding('Deze foto is groter dan 25 MB. Kies een kleinere foto.', true);
        return;
      }
      fotoMelding('Kleuren worden uitgelezen…', false);

      // Eén blob-URL voor zowel de analyse als de voorbeeldweergave. Die URL wijst
      // naar het bestand in het geheugen van deze tab; er gaat geen byte de deur uit.
      var url = URL.createObjectURL(bestand);
      var img = new Image();
      img.onload = function () {
        var kleuren = dominanteKleuren(img, 6);
        if (!kleuren.length) {
          URL.revokeObjectURL(url);
          fotoMelding('We konden geen kleuren uit deze foto halen. Probeer een andere foto.', true);
          return;
        }
        toonDominant(kleuren, url);
        fotoMelding('Klik op een kleur om die te gebruiken.', false);
        duw('kleur_match_foto', { aantal_kleuren: kleuren.length });
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        fotoMelding('Deze afbeelding kon niet geopend worden. Probeer een JPG of PNG.', true);
      };
      img.src = url;
    }

    var vorigeFotoUrl = null;

    function toonDominant(kleuren, blobUrl) {
      if (!dom.domRij || !dom.fotoRij) { URL.revokeObjectURL(blobUrl); return; }
      dom.fotoRij.hidden = false;
      if (dom.fotoPrev) dom.fotoPrev.src = blobUrl;
      if (vorigeFotoUrl) URL.revokeObjectURL(vorigeFotoUrl);
      vorigeFotoUrl = blobUrl;
      dom.domRij.innerHTML = '';
      kleuren.forEach(function (k, i) {
        var knop = document.createElement('button');
        knop.type = 'button';
        knop.className = 'km26-dom';
        knop.style.background = k.hex;
        knop.setAttribute('aria-pressed', 'false');
        knop.setAttribute('aria-label', 'Kleur ' + k.hex + ' uit je foto (' + Math.round(k.deel * 100) + '% van het beeld)');
        knop.title = k.hex + ' · ' + Math.round(k.deel * 100) + '% van het beeld';
        knop.addEventListener('click', function () {
          alle(dom.domRij, '.km26-dom').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
          knop.setAttribute('aria-pressed', 'true');
          zetKleur(k.hex, 'foto');
        });
        dom.domRij.appendChild(knop);
        if (i === 0) knop.setAttribute('aria-pressed', 'true');
      });
      if (kleuren[0]) zetKleur(kleuren[0].hex, 'foto');
    }

    if (dom.bestand) {
      dom.bestand.addEventListener('change', function () {
        verwerkBestand(dom.bestand.files && dom.bestand.files[0]);
      });
    }
    if (dom.drop) {
      ['dragenter', 'dragover'].forEach(function (n) {
        dom.drop.addEventListener(n, function (e) { e.preventDefault(); dom.drop.classList.add('is-over'); });
      });
      ['dragleave', 'drop'].forEach(function (n) {
        dom.drop.addEventListener(n, function (e) { e.preventDefault(); dom.drop.classList.remove('is-over'); });
      });
      dom.drop.addEventListener('drop', function (e) {
        var bestanden = e.dataTransfer && e.dataTransfer.files;
        if (bestanden && bestanden.length) verwerkBestand(bestanden[0]);
      });
    }

    /* (kopieerknoppen) */

    alle(root, '.km26-kopieer').forEach(function (knop) {
      knop.addEventListener('click', function () {
        var waarde = knop.dataset.waarde || '';
        if (!waarde) return;
        var oud = knop.textContent;
        function bevestig() {
          knop.textContent = 'Gekopieerd';
          setTimeout(function () { knop.textContent = oud; }, 1600);
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(waarde).then(bevestig, function () { /* stil */ });
          return;
        }
        var tijdelijk = document.createElement('textarea');
        tijdelijk.value = waarde;
        tijdelijk.style.position = 'fixed';
        tijdelijk.style.opacity = '0';
        document.body.appendChild(tijdelijk);
        tijdelijk.select();
        try { document.execCommand('copy'); bevestig(); } catch (e) { /* stil */ }
        document.body.removeChild(tijdelijk);
      });
    });

    /* (leadformulier) */

    function formMelding(soort, kop, body) {
      if (!dom.formMelding) return;
      dom.formMelding.hidden = false;
      dom.formMelding.className = 'km26-formmelding km26-formmelding--' + soort;
      dom.formMelding.innerHTML = '';
      var sterk = document.createElement('strong');
      sterk.textContent = kop;
      dom.formMelding.appendChild(sterk);
      dom.formMelding.appendChild(document.createTextNode(body));
      dom.formMelding.setAttribute('role', soort === 'err' ? 'alert' : 'status');
    }

    if (dom.form) {
      dom.form.addEventListener('submit', function (e) {
        e.preventDefault();

        var naam = el(dom.form, '[name="name"]');
        var mail = el(dom.form, '[name="email"]');
        var wensen = el(dom.form, '[name="colorWishes"]');
        var consent = el(dom.form, '[name="consent"]');
        var honeypot = el(dom.form, '[name="website"]');
        var knop = el(dom.form, '.km26-verstuur');

        var fout = null;
        [naam, mail].forEach(function (v) { if (v) veldMelding(v, ''); });
        if (consent) consent.setAttribute('aria-invalid', 'false');

        if (naam && !naam.value.trim()) {
          veldMelding(naam, 'Vul je naam in.');
          fout = fout || naam;
        }
        var mailWaarde = mail ? mail.value.trim() : '';
        if (mail && (!mailWaarde || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(mailWaarde))) {
          veldMelding(mail, 'Vul een geldig e-mailadres in; daar sturen we het rapport naartoe.');
          fout = fout || mail;
        }
        if (!consent || !consent.checked) {
          var consentMelding = el(dom.form, '.km26-consentmelding');
          if (consentMelding) tekst(consentMelding, 'Zonder je toestemming mogen we je geen rapport sturen.');
          if (consent) consent.setAttribute('aria-invalid', 'true');
          fout = fout || consent;
        } else {
          var cm = el(dom.form, '.km26-consentmelding');
          if (cm) tekst(cm, '');
        }

        if (fout) {
          formMelding('err', 'Nog even controleren', 'Er ontbreekt nog iets hieronder; we hebben het gemarkeerd.');
          if (fout.focus) fout.focus();
          return;
        }

        // Honeypot: alleen een bot vult dit veld. We versturen niets en tonen
        // hetzelfde succesbericht, zodat de bot geen signaal krijgt.
        if (honeypot && honeypot.value.trim() !== '') {
          formMelding('ok', 'Bedankt, je aanvraag staat genoteerd.',
            'Je ontvangt je persoonlijke kleurrapport binnen één werkdag per e-mail.');
          dom.form.reset();
          return;
        }

        if (!API) {
          formMelding('err', 'Versturen lukt nu niet',
            'Deze pagina heeft nog geen koppeling met ons systeem. Mail je vraag zolang naar info@tafelrokkenshop.nl, dan pakken we het handmatig op.');
          return;
        }

        if (knop) { knop.disabled = true; knop.textContent = 'Versturen…'; }
        formMelding('ok', 'Bezig met versturen…', 'Even geduld.');

        postJson(API + '/api/public/color-tool/lead', {
          name: naam.value.trim(),
          email: mailWaarde,
          hex: staat.hex,
          // Zelfde systeem als bij /match, anders noemt het kleurrapport een
          // andere code dan de bezoeker op de pagina zag.
          pantoneSystem: staat.systeem,
          colorWishes: wensen ? wensen.value.trim() : '',
          consent: true,
          source: 'kleur-match',
          website: ''
        }, 12000).then(function (antwoord) {
          if (knop) { knop.disabled = false; knop.textContent = 'Stuur mijn kleurrapport'; }
          if (!antwoord || antwoord.ok !== true) {
            formMelding('err', 'Versturen is niet gelukt',
              'Probeer het zo nog eens, of mail je kleurcode naar info@tafelrokkenshop.nl, dan pakken we het handmatig op.');
            return;
          }
          formMelding('ok', 'Bedankt, je aanvraag staat genoteerd.',
            'Je ontvangt je persoonlijke kleurrapport binnen één werkdag per e-mail, met de codes en de rokken die het dichtst bij ' + staat.hex + ' komen.');
          dom.form.reset();
          toonPreview();
          // form.reset() zet het verborgen veld terug op de waarde uit de Liquid.
          zetSysteem(staat.systeem, false);
          duw('generate_lead', { lead_bron: 'kleur-match', kleur_hex: staat.hex, pantone_system: staat.systeem });
        }, function (e) {
          if (knop) { knop.disabled = false; knop.textContent = 'Stuur mijn kleurrapport'; }
          // De backend staat 5 aanvragen per minuut per IP toe; daarna 429.
          if (e && e.status === 429) {
            formMelding('err', 'Even rustig aan',
              'Je hebt net al een paar aanvragen verstuurd. Wacht een minuutje en probeer het dan nog eens; je gegevens staan nog ingevuld.');
            return;
          }
          formMelding('err', 'Versturen is niet gelukt',
            'We konden ons systeem niet bereiken. Probeer het zo nog eens, of mail je kleurcode naar info@tafelrokkenshop.nl.');
        });
      });
    }

    /* (beginstand) */

    // Keuzeveld, placeholder en het verborgen leadveld op de onthouden voorkeur
    // zetten voordat er iets getekend wordt.
    zetSysteem(staat.systeem, false);
    zetWielType(staat.wielType, false);
    toonPreview();
    berekenLokaal();
    statusBlok('Kies eerst een kleur',
      'Zodra je hierboven een kleur kiest, uploadt of een code invult, zoeken we de tafelrokken en toppers die er het dichtst bij komen.');
    if (dom.combi) dom.combi.hidden = true;
  }

  function start() {
    alle(document, '[data-kleur-match]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Theme-editor: opnieuw initialiseren nadat een sectie hertekend is.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-kleur-match]') : null;
    if (root) init(root);
  });
})();
